import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Descriptions, Table, Tag, Button, Space, useApp, Typography, Row, Col, Statistic, Tabs,
  Timeline, Empty, Steps, Divider, Tooltip, Modal, Input, Textarea, Popconfirm, theme, Spin, Result, Alert, cn,
} from '../../ui';
import type { ButtonProps, TagTone } from '../../ui';
import {
  ShoppingCart, CheckCircle2, CalendarCheck, Inbox, Car, Flag,
  FileCheck, Ban, FileText, Clock, FlaskConical, User,
  Send, XCircle, Repeat, Undo2, ArrowLeftRight, Upload, Wallet, MapPin, Store, Globe, ShieldCheck, AlertTriangle,
  Smartphone, MessageCircle, MessageSquare, Truck, IndianRupee,
  ArrowRight, ArrowDown, ExternalLink, Eye,
} from 'lucide-react';
import { api, request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { RaiseReturnModal } from './RaiseReturnModal';
import { CreditNoteModal } from '../../components/CreditNoteModal';
import { RetailerLink, UserLink, PersonLink } from '../../components/EntityLinks';
import { usePermissionChecker, useAuthStore, useCanSeeWarehouses, usePermission, useHasRole } from '../../store/auth';
import { brand } from '../../assets/brand';
import { fmtQty, variantText } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { previewPdf } from '../../lib/pdf';
import { AttachmentCell, AttachmentViewer } from '../../components/AttachmentPreview';
import { ChannelTag } from '../../components/ChannelTag';
import { RoleBadge } from '../../components/RoleBadge';
import { DispatchModal } from './DispatchModal';
import CancelOrderModal, { CancelBreakdown } from './CancelOrderModal';
import { StockPlanPanel } from './StockPlanPanel';
import { WarehouseCoveragePanel } from './WarehouseCoveragePanel';
import { FULFILLMENT_V2_ENABLED, RETURNS_LIFECYCLE_ENABLED } from '../../lib/flags';
import { ApproveOrderModal } from './ApproveOrderModal';
import { PayOrderModal } from './PayOrderModal';
import { RefundPaymentModal } from './RefundPaymentModal';
import { SettleAdvanceModal } from './SettleAdvanceModal';
import { AdvanceCdPanel } from './AdvanceCdPanel';
import { InvoiceUploadModal } from '../billing/InvoiceUploadModal';
import { PaymentReviewModal } from '../billing/PaymentReviewModal';
import { FileUploadField } from '../../components/FileUploadField';
import { formatIst, formatIstDate } from '../../lib/time';
import { inr2, plural } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { orderStatusLabel, orderTimelineStep } from '../../lib/statusTone';

type GatedButtonProps = ButtonProps & { allowed: boolean; reason: string };

/** A Button that, when the user lacks the required permission, renders disabled
 *  with a tooltip explaining why — instead of letting the click hit the API and
 *  bounce back as a raw "Missing permission(s)" error toast.
 *
 *  forwardRef is REQUIRED: this is used as a Popconfirm/Popover child (Radix `asChild`),
 *  which merges a ref + onClick onto it. A plain function component would swallow that ref,
 *  leaving the confirm popup unanchored so it never opens — the click would do nothing. */
const GatedButton = forwardRef<HTMLButtonElement, GatedButtonProps>(function GatedButton(
  { allowed, reason, children, style, ...rest }, ref,
) {
  if (allowed) return <Button ref={ref} {...rest} style={style}>{children}</Button>;
  return (
    <Tooltip label={reason}>
      <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
        <Button ref={ref} {...rest} disabled style={{ ...style, pointerEvents: 'none' }}>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
});

/**
 * What an invoice's `verificationStatus` MEANS, in words.
 *
 * The raw enum was printed as-is — so a shipment line read
 * "TA/2026/09/00022 · ₹1,917 [VERIFIED]" directly under a "Pay ₹1,917" button, with Payments (0)
 * below. Asked, reasonably: *"what verified here?"* — a bare VERIFIED sitting after a MONEY figure
 * reads as "this ₹1,917 is verified", i.e. received. It is nothing of the kind: it is the result of
 * comparing the uploaded Tally invoice's FIGURES against what should have been billed (the order for
 * WHOLE, the shipment for PER_SHIPMENT — billing.service.compareInvoiceWithExpected).
 *
 * So the label names the document and the check, never the amount beside it.
 */
const INVOICE_VERIFY: Record<string, { label: string; color: string; hint: string }> = {
  VERIFIED: { label: 'Invoice matches', color: 'green',
    hint: 'The uploaded Tally invoice’s figures match what should have been billed. This is not a payment.' },
  MISMATCH: { label: 'Invoice differs', color: 'red',
    hint: 'The uploaded Tally invoice does not match what should have been billed — an admin must approve the difference before dispatch.' },
  OVERRIDDEN: { label: 'Mismatch approved', color: 'orange',
    hint: 'The invoice did not match, and an admin approved the difference with a recorded reason.' },
  UNVERIFIED: { label: 'Not checked yet', color: 'default',
    hint: 'The uploaded invoice has not been compared against the order yet.' },
};
const invoiceVerify = (v?: string | null) => INVOICE_VERIFY[v ?? 'UNVERIFIED'] ?? INVOICE_VERIFY.UNVERIFIED;

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default', PLACED: 'blue', AWAITING_PRODUCTION: 'magenta', IN_PRODUCTION: 'purple', AWAITING_TRANSFER: 'geekblue', APPROVED: 'cyan', ACCEPTED: 'purple', PACKED: 'gold', DISPATCHED: 'orange',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'default',
  ON_HOLD: 'orange', CORRECTION_REQUIRED: 'gold', REJECTED: 'red', BACKORDER: 'volcano',
};

// Order statuses in plain English — one shared map (lib/statusTone) so the steps strip, the header
// pill, the timeline and the orders register cannot drift apart. Notably ACCEPTED reads
// "In fulfillment": see the map for why the stored code is left alone.
const statusLabel = orderStatusLabel;

const STATUS_ICON: Record<string, JSX.Element> = {
  DRAFT: <FileText size={16} />,
  PLACED: <ShoppingCart size={16} />,
  AWAITING_PRODUCTION: <Clock size={16} />,
  IN_PRODUCTION: <FlaskConical size={16} />,
  AWAITING_TRANSFER: <Clock size={16} />,
  APPROVED: <CheckCircle2 size={16} />,
  ACCEPTED: <CalendarCheck size={16} />,
  PACKED: <Inbox size={16} />,
  DISPATCHED: <Car size={16} />,
  DELIVERED: <Flag size={16} />,
  INVOICED: <FileCheck size={16} />,
  CANCELLED: <Ban size={16} />,
  CLOSED: <CheckCircle2 size={16} />,
  ON_HOLD: <Clock size={16} />,
  CORRECTION_REQUIRED: <FileText size={16} />,
  REJECTED: <XCircle size={16} />,
  BACKORDER: <Clock size={16} />,
};

// The 'CANCELLED' entry drives the Cancel / short-close button. FULL cancellation is valid while nothing has
// left the building — DRAFT / PLACED / ON_HOLD / CORRECTION_REQUIRED and (since DEF-007) APPROVED / ACCEPTED /
// PACKED, the last of which returns the stock committed at pack. Once a shipment is DISPATCHED or DELIVERED the
// entry survives only so the SHORT-CLOSE path (drop the un-shipped remainder) still has a button. Which of the
// two the button performs is decided by canFullCancelNow / canShortCloseNow below, and both mirror the backend
// guards so the button never appears where the API would refuse it.
const NEXT: Record<string, string[]> = {
  PLACED: ['APPROVED', 'CANCELLED'],
  AWAITING_PRODUCTION: ['APPROVED', 'PACKED', 'CANCELLED'],
  // Being produced (a run exists). Reverts/heals automatically; manual option is forward + short-close.
  IN_PRODUCTION: ['APPROVED', 'PACKED', 'CANCELLED'],
  // Held for consolidation: no manual transition — it self-heals back when transfers land.
  AWAITING_TRANSFER: [],
  // BACKORDER: park an approved order that is SHORT until stock lands, instead of forcing a
  // transfer or splitting the consignment. Offered only while it is actually short (below).
  APPROVED: ['ACCEPTED', 'BACKORDER', 'CANCELLED'],
  // Packing is done via the "Pick & pack" batch picker (it commits stock), not a bare status flip.
  ACCEPTED: ['BACKORDER', 'CANCELLED'],
  PACKED: ['DISPATCHED', 'CANCELLED'],
  // Dispatched: no cancel and no short-close (goods are out) — only forward to Delivered.
  DISPATCHED: ['DELIVERED'],
  // New flow closes directly (invoice already raised before dispatch); legacy goes
  // via INVOICED. The CLOSED-from-DELIVERED button is filtered to new-flow orders below.
  DELIVERED: ['INVOICED', 'CLOSED'],
  INVOICED: ['CLOSED'],
  // New approval sub-states. The reason-based reject/hold/correction live on the Approval Queue;
  // these generic transitions let a held / backordered order move forward from the detail page.
  ON_HOLD: ['APPROVED', 'CANCELLED'],
  CORRECTION_REQUIRED: ['CANCELLED'],
  BACKORDER: ['APPROVED', 'CANCELLED'],
  REJECTED: [],
};

const STEPS = ['PLACED', 'APPROVED', 'ACCEPTED', 'PACKED', 'DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED'];

// Friendlier labels for the status-advance buttons.
const ACTION_LABEL: Record<string, string> = {
  APPROVED: 'Approve',
  ACCEPTED: 'Start fulfillment',
  PACKED: 'Pick & pack',
  DELIVERED: 'Mark Delivered',
  INVOICED: 'Mark Invoiced',
  CLOSED: 'Close order',
  BACKORDER: 'Hold for stock',
  CANCELLED: 'Cancel',
};

const inr = (v: any) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
// Format a plain 'YYYY-MM-DD' (e.g. batch expiry) as '27 Jun 2029' without timezone shifting.
const fmtDateShort = (d?: string | null) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1];
  return mon ? `${dd} ${mon} ${y}` : String(d);
};
// Compact elapsed-time label for the fulfilment-timing panel: '12 min', '2h 8m', '1d 3h'.
const fmtDuration = (ms: number) => {
  if (!ms || ms < 0) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return '<1 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24), hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
};

/** A payment's proof files, tolerating the pre-multi-attachment single-URL column. */
const paymentProofs = (p: any): string[] =>
  (p?.attachmentUrls?.length ? p.attachmentUrls : (p?.attachmentUrl ? [p.attachmentUrl] : [])).filter(Boolean);

const SHIPMENT_COLOR: Record<string, string> = {
  PACKED: 'gold', DISPATCHED: 'orange', DELIVERED: 'green', CANCELLED: 'red',
};

// Stateful POD picker for the "Mark delivered" dialog. Modal.confirm renders its content
// once, so a bare closure value never re-renders — this holds the uploaded URL in state so
// the FileUploadField shows its file chip AND we render a clear attached/not-attached status.
function PodPicker({ onChange }: { onChange: (url: string) => void }) {
  const [url, setUrl] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      <div className="text-xs" style={{ color: 'var(--gb-text-muted)', marginBottom: 6 }}>
        Optional: attach POD (proof of delivery) — signed challan photo or PDF.
      </div>
      <FileUploadField
        category="pod"
        buttonLabel={url ? 'Replace POD' : 'Upload POD'}
        value={url}
        onChange={(u) => { setUrl(u); onChange(u); }}
      />
      <div className="text-sm" style={{ marginTop: 8 }}>
        {url
          ? <span style={{ color: 'var(--color-success-600)' }}><CheckCircle2 size={16} /> POD attached</span>
          : <span style={{ color: '#bfbfbf' }}><XCircle size={16} /> No POD attached yet</span>}
      </div>
    </div>
  );
}

// Blob (file) downloads return a Blob body even on error, so a JSON {error:{message}} can't be read
// off e.response.data directly — this unwraps it to the real server message (e.g. why a proforma failed).
async function readDownloadError(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  try {
    if (data instanceof Blob) {
      const txt = await data.text();
      try { return JSON.parse(txt)?.error?.message ?? fallback; } catch { return fallback; }
    }
    return data?.error?.message ?? fallback;
  } catch { return fallback; }
}

/** One label/value pair in the redesigned Overview (uppercase label above the value). */
function OField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="type-overline text-subtle">{label}</dt>
      <dd className="mt-1 text-sm text-content">{children ?? <span className="text-subtle">—</span>}</dd>
    </div>
  );
}

/**
 * Heading for one block of the Overview.
 *
 * The blocks were told apart only by a grey `type-overline` and a hairline rule, so "Customer" and
 * "Order details" read as two more field labels in a page already full of them — nothing marked them
 * as the START of a group. This is the same masthead recipe the settings form already uses
 * (`admin/BankDetailsPage.tsx` FieldGroup): a tinted icon chip carrying the block's own colour, then
 * the title in content weight. The tint is what makes the group scannable; the icon says which one.
 */
const SECTION_CHIP: Record<string, string> = {
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  sky: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  gold: 'bg-harvest-50 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
};

/*
 * A block of the Documents tab: the head, then its table on a surface PLATE.
 *
 * The tab was three bare `Typography.Title` lines with tables under them, laid straight onto the
 * card, which itself sits on the page's green wash — pale green table headers on a pale green
 * ground, nothing bounded, everything the same value. Wrapping each table in `bg-surface` with a
 * border and one step of elevation is what separates them: the plate has to step UP off the wash,
 * exactly as the expanded-row blocks on the orders register do.
 */
function DocSection({ icon, tone, title, children }: {
  icon: ReactNode; tone?: keyof typeof SECTION_CHIP; title: string; children: ReactNode;
}) {
  return (
    <section className="mb-5 last:mb-0">
      <OSectionHead icon={icon} tone={tone} title={title} />
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">{children}</div>
    </section>
  );
}

function OSectionHead({ icon, tone = 'brand', title, hint }: {
  icon: ReactNode; tone?: keyof typeof SECTION_CHIP; title: string; hint?: string;
}) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      <span className={cn('grid size-8 shrink-0 place-items-center rounded-card [&>svg]:size-4', SECTION_CHIP[tone])} aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <h4 className="type-label-strong text-content">{title}</h4>
        {hint && <p className="mt-0.5 type-caption text-muted">{hint}</p>}
      </div>
    </div>
  );
}


/**
 * A person in a detail field: their name, linked where it may be, with the coloured role pill beside
 * it — the way the app names people everywhere else (the orders LIST already does exactly this).
 * Without the pill an order's three names — who placed it, whose customer it is, who approved it —
 * were three identical pieces of green text, and "in what capacity" is half of what they say.
 *
 * `inline-flex` keeps the name and its pill together: they are one label, and a wrap that left the
 * badge stranded on the next line would read as a badge belonging to nothing.
 */
function Person({ id, role, retailerId, children }: {
  /** The `users` row id of the person named — without it the link can only reach the bare list. */
  id?: string | number | null;
  role?: string | null;
  /** This order's dealer, so a RETAILER login opens the dealer's page rather than a staff one. */
  retailerId?: string | number | null;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <PersonLink id={id} role={role} retailerId={retailerId}>{children}</PersonLink>
      <RoleBadge role={role} />
    </span>
  );
}

/** One row of the invoice-style Amount summary panel (label left, amount right-aligned). */
function AmountRow({ label, value, tone, strong }: { label: ReactNode; value: ReactNode; tone?: 'danger'; strong?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between gap-4', strong ? 'py-0.5' : 'py-2')}>
      <span className={cn('text-sm', strong ? 'font-semibold text-content' : 'text-muted')}>{label}</span>
      <span
        className={cn('tabular-nums', strong ? 'type-metric-sm' : cn('text-sm', tone === 'danger' ? 'text-danger-600' : 'text-content'))}
        style={strong ? { color: brand.primary } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

/** GST rate suffix for a tax row, derived as tax ÷ taxable → e.g. " · 9%". Empty when there's no tax
 *  or no taxable base. Exact for a single-rate order; a blended effective rate for mixed-rate lines. */
function gstRate(amt: any, base: any): string {
  const a = Number(amt), b = Number(base);
  if (!(a > 0) || !(b > 0)) return '';
  const p = +((a / b) * 100).toFixed(2);
  return ` (${p}%)`;
}

export function OrderDetailPage() {
  const canSeeWarehouses = useCanSeeWarehouses();
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message } = useApp();
  const qc = useQueryClient();
  const [proofView, setProofView] = useState<{ files: string[]; label: string } | null>(null);
  const [proofAt, setProofAt] = useState(0);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [refundFor, setRefundFor] = useState<any>(null);
  // The payment being verified from this page — the same review dialog Billing → Payments opens.
  const [payReviewFor, setPayReviewFor] = useState<any>(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [invoiceForShipment, setInvoiceForShipment] = useState<any | null>(null); // per-shipment (PER_SHIPMENT) invoice upload
  const [invoicePickerOpen, setInvoicePickerOpen] = useState(false); // header shortcut when >1 packed shipment awaits an invoice
  const [activeTab, setActiveTab] = useState('overview');
  const [overrideFor, setOverrideFor] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const { data: o, isLoading, isError } = useQuery({
    queryKey: ['order', id],
    queryFn: () => request<any>({ url: `/orders/${id}` }),
  });

  const { data: timeline } = useQuery({
    queryKey: ['order-timeline', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/timeline` }),
    enabled: !!id,
  });

  const { data: invoices, isFetched: invoicesFetched } = useQuery({
    queryKey: ['order-invoices', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/invoices` }),
    enabled: !!id,
  });

  const { data: shipments, isFetched: shipmentsFetched } = useQuery({
    queryKey: ['order-shipments', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/shipments` }),
    enabled: !!id,
  });

  // Credit / debit notes raised anywhere on this order (across its invoices + sales returns) — for
  // traceability. Read with the standalone hook because `hasPerm` is declared further down.
  // Needs INVOICE.VIEW: a Warehouse Manager opens this page to pack and dispatch but has
  // no invoice access, so asking unconditionally 403'd on every order they opened.
  const canViewInvoices = usePermission('INVOICE.VIEW');
  // Whether this viewer DECIDES cancellation requests rather than raising them. Declared HERE with
  // the other permission hooks: the page returns early while loading, and a hook called after that
  // point runs on some renders and not others, which blanks the screen.
  const canDecideCancel = usePermission('ORDER.CANCEL.APPROVE');
  const canDecideReject = usePermission('ORDER.REJECT.APPROVE');
  // A customer reads their own order's PROGRESS, not the company's internal performance against
  // it. See common/scope/customer-view.ts on the backend for the same rule applied to actors.
  const isCustomer = useHasRole('RETAILER', 'FARMER');
  const { data: orderNotes } = useQuery({
    queryKey: ['order-credit-notes', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/credit-notes` }),
    enabled: !!id && canViewInvoices,
  });
  const [noteDrawerId, setNoteDrawerId] = useState<string | null>(null);

  // Remaining (un-shipped) quantity per order item — used to decide whether
  // another dispatch is still possible.
  const { data: pending } = useQuery({
    queryKey: ['order-pending', id],
    queryFn: () => request<Record<string, number>>({ url: `/orders/${id}/shipments/pending` }),
    enabled: !!id,
  });

  // Resolve ids → names for a professional Overview (permission-gated; falls
  // back to #id when a role can't fetch the master list).
  const hasPerm = usePermissionChecker();
  // Fulfillment roles (Warehouse Manager) don't see financials — hide money panels, totals,
  // per-line prices/taxes and the Payments tab. Consistent with their no-PAYMENT.* permission set.
  const userRole = useAuthStore((s) => s.user?.role);
  const canSeeMoney = userRole !== 'WAREHOUSE_MANAGER';
  // Who is reading — needed to tell "the person this order was sent back to" from everyone else
  // (see canResubmit below). A Sales Person holds no ORDER.APPROVE/EDIT, so identity is the gate.
  const myId = useAuthStore((s) => s.user?.id);
  // Action gating — mirrors the backend @RequirePermissions on each endpoint, so
  // a role only ever sees an action it can actually perform (disabled + reason
  // otherwise, instead of a raw 403 after the click).
  const canEdit = hasPerm('ORDER.EDIT'); // dispatch, cancel shipment
  const canPack = hasPerm('WAREHOUSE.PACK.COMPLETE') || canEdit; // WM (or admin) packs + dispatches their warehouse's orders
  const canAccept = hasPerm('WAREHOUSE.PACK.START') || canEdit; // WM (or admin) accepts the routed order
  // Dispatch planning (warehouse selection / stock plan / consolidation) — granular perm OR ORDER.EDIT.
  const canPlan = hasPerm('ORDER.DISPATCH.PLAN') || canEdit;
  const canAddStock = hasPerm('STOCK.MASTER.ADD'); // Phase 2 — "Add stock" → master-warehouse stock (auto-GRN)
  const canReportShortfall = hasPerm('WAREHOUSE.PACK.START') || hasPerm('WAREHOUSE.PACK.COMPLETE') || hasPerm('ORDER.DISPATCH.PLAN') || hasPerm('ORDER.EDIT'); // Phase 2
  const canDeliver = hasPerm('ORDER.DELIVER'); // mark delivered (field action — SP/SH too)
  const canInvoice = hasPerm('INVOICE.CREATE');
  // A Warehouse Manager can also upload/verify the Tally invoice for their own warehouse's order.
  const canUploadInvoice = canInvoice || hasPerm('WAREHOUSE.PACK.COMPLETE');
  // Granular Tally-invoice gate (mirrors the backend): VERIFY falls back to INVOICE.CREATE; OVERRIDE is admin-only.
  const canVerify = hasPerm('INVOICE.VERIFY') || canInvoice || hasPerm('WAREHOUSE.PACK.COMPLETE');
  const canOverride = hasPerm('INVOICE.OVERRIDE');
  const canVoidInvoice = hasPerm('INVOICE.CANCEL'); // void a recorded Tally invoice
  const canProforma = hasPerm('ORDER.PROFORMA.VIEW'); // proforma download — staff + the order's own retailer
  const canCreate = hasPerm('ORDER.CREATE'); // reorder
  // PATCH /orders/:id/status is gated by ORDER.APPROVE OR ORDER.EDIT.
  const canChangeStatus = hasPerm('ORDER.APPROVE') || canEdit;
  const canCancel = hasPerm('ORDER.CANCEL') || canChangeStatus; // retailer / sales person / sales head can cancel too
  const canPay = hasPerm('ORDER.PAY'); // retailer self-pay, or SP/SH on behalf — submits a payment for the order
  const canRefund = hasPerm('PAYMENT.REFUND'); // Accounts/Admin/SA — record a refund of an advance on a cancelled order
  // A return is PLACED only by the retailer / SP / SH (per the spec) — approvers (Admin/SA), Accounts and the
  // Who may raise a return: gated PURELY by the RETURN.CREATE permission — internal staff (Admin, Super Admin,
  // Sales Person, Sales Head) hold it; the RETAILER, ACCOUNTS and Warehouse Manager roles deliberately do NOT,
  // so the "Raise return" button is hidden for them (the retailer can only VIEW a return raised on their behalf).
  const canRaiseReturn = hasPerm('RETURN.CREATE');
  const { data: paySummary } = useQuery({
    queryKey: ['order-pay-summary', id],
    queryFn: () => request<any>({ url: `/orders/${id}/payment-summary` }),
    // Drives the close gate / due display for every viewer who may see money. A Warehouse Manager may
    // not: the page already hides prices from them, and the service refuses the call (they have no
    // retailer scope), so asking anyway just logged a 403 on every order they opened.
    enabled: !!id && canSeeMoney,
  });
  // Advance excess left after a short-close (retailer paid for more than the order was finally billed).
  // Accounts settle it (refund or keep-as-credit). Fetched for money-viewers who can see/act on refunds.
  const canViewAdvance = canRefund || hasPerm('PAYMENT.VIEW');
  const { data: advanceExcess } = useQuery({
    queryKey: ['order-advance-excess', id],
    queryFn: () => request<any>({ url: `/orders/${id}/advance-excess` }),
    enabled: !!id && canSeeMoney && canViewAdvance,
  });
  // This order's payments + their approval status — the retailer's payment history for the order.
  const { data: orderPayments } = useQuery({
    queryKey: ['order-payments', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/payments` }),
    enabled: !!id && canSeeMoney,   // same reason as the payment summary above
  });
  const { data: retailers } = useQuery({
    queryKey: ['order-detail-retailers'],
    queryFn: () => requestAllPages('/retailers'),
    enabled: hasPerm('RETAILER.VIEW'),
  });
  const { data: users } = useQuery({
    queryKey: ['order-detail-users'],
    queryFn: () => requestAllPages('/users'),
    enabled: hasPerm('USER.VIEW'),
  });
  const { data: warehouses } = useQuery({
    queryKey: ['order-detail-warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Names the dispatch warehouse on the order. Was additionally gated on WAREHOUSE.VIEW, which a
    // Warehouse Manager does not hold — so the one role that packs and dispatches this order saw a raw
    // id where the warehouse should be. useCanSeeWarehouses matches what /warehouses/my accepts.
    enabled: canSeeWarehouses,
  });
  const retailerName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), `${r.companyName} (${r.code})`);
    return m;
  }, [retailers]);
  const userName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users?.data ?? []) m.set(String(u.id), u.fullName);
    return m;
  }, [users]);
  const warehouseName = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of warehouses ?? []) m.set(String(w.id), `${w.name} (${w.code})`);
    return m;
  }, [warehouses]);
  // Product names come straight from the order's own items (no extra fetch / permission needed),
  // so shipment-line drilldowns show names instead of raw product IDs for every viewer.
  const productName = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of ((o as any)?.items ?? [])) {
      const v = variantText(it.packSize, it.uom, it.variantLabel);
      m.set(String(it.productId), `${it.productNameSnapshot}${v ? ` ${v}` : ''}`);
    }
    return m;
  }, [o]);
  const nameOf = (map: Map<string, string>, idv: any) =>
    idv ? (map.get(String(idv)) ?? `#${idv}`) : '—';

  // Full lifecycle for the Timeline tab: order status changes + advance/verify/reject/refund payment
  // events + invoices, merged in chronological order — so anyone (even a newbie) can trace the order
  // end-to-end at a glance, with who/when/amount and a proof link on each step.
  const lifecycleEvents = useMemo(() => {
    const ev: Array<{
      at: any; color: string; icon: JSX.Element; title: any;
      who?: string | null;
      // The actor's ROLE, badged the way every other list in the app badges a name.
      whoRole?: string | null;
      note?: any; noteStrong?: boolean;
      // ALL the proof files, not just the first: "View proof" hid the rest of a multi-photo receipt
      // and made the reader open a tab to see whether it was even the right document.
      proofs?: string[];
    }> = [];
    for (const t of (timeline ?? [])) {
      // A partial dispatch/delivery keeps the order OPEN (a part-shipped order stays PACKED). The row keeps the
      // order's real statuses and names the shipment's step (DEF-062; older rows have no "from" status) — render it
      // as an amber "Partially dispatched/delivered" so it doesn't read like the whole order is done.
      const { step, partialShipment: isPartialShip } = orderTimelineStep(t);
      const baseIcon = STATUS_ICON[step] ?? <Clock size={16} />;
      ev.push({
        at: t.at,
        color: isPartialShip ? '#faad14' : (STATUS_COLOR[t.toStatus] === 'red' ? 'red' : STATUS_COLOR[t.toStatus] === 'green' ? 'green' : 'blue'),
        icon: isPartialShip ? <span style={{ color: '#faad14' }}>{baseIcon}</span> : baseIcon,
        title: isPartialShip
          ? <b style={{ color: 'var(--color-warning-600)' }}>{step === 'DISPATCHED' ? 'Partially dispatched' : 'Partially delivered'}</b>
          : <span>{t.fromStatus ? <span style={{ color: '#999' }}>{statusLabel(t.fromStatus)} → </span> : null}<b>{statusLabel(t.toStatus)}</b></span>,
        who: t.changedByName, whoRole: t.changedByRoleCode, note: t.notes, noteStrong: isPartialShip,
      });
    }
    for (const p of (orderPayments ?? [])) {
      if (p.fromCredit) {
        // Applied account credit — no cash, no approval step; it settles the order immediately.
        ev.push({
          at: p.createdAt ?? p.paymentDate, color: 'green', icon: <Wallet size={16} />,
          title: <span><b>Account credit applied</b> · {inr2(p.amount)} <Tag color="green" style={{ margin: 0 }}>Credit</Tag></span>,
          who: p.submittedByName, whoRole: p.submittedByRoleCode,
        });
        continue;
      }
      ev.push({
        at: p.createdAt ?? p.paymentDate, color: 'blue', icon: <Wallet size={16} />,
        title: <span>Advance <b>submitted</b> · {inr2(p.amount)} <Tag style={{ margin: 0 }}>{p.mode}</Tag></span>,
        who: p.submittedByName, whoRole: p.submittedByRoleCode,
        note: p.approvalStatus === 'PENDING' ? 'Awaiting verification' : undefined,
        proofs: p.attachmentUrls?.length ? p.attachmentUrls : (p.attachmentUrl ? [p.attachmentUrl] : []),
      });
      if (p.approvalStatus === 'APPROVED' && p.approvedAt) {
        ev.push({ at: p.approvedAt, color: 'green', icon: <CheckCircle2 size={16} />,
          title: <span>Advance <b>verified</b> · {inr2(p.amount)}</span>, who: p.approvedByName, whoRole: p.approvedByRoleCode });
      }
      if (p.approvalStatus === 'REJECTED') {
        ev.push({ at: p.createdAt ?? p.paymentDate, color: 'red', icon: <XCircle size={16} />,
          title: <span>Advance <b>rejected</b> · {inr2(p.amount)}</span>, note: p.rejectedReason });
      }
      if (p.status === 'REFUNDED' && p.refundedAt) {
        ev.push({ at: p.refundedAt, color: 'red', icon: <Repeat size={16} />,
          title: <span>Refund <b>processed</b> · {inr2(p.refundAmount ?? p.amount)} <Tag style={{ margin: 0 }}>{p.refundMode}</Tag></span>,
          who: p.refundedByName, whoRole: p.refundedByRoleCode, note: p.refundReason,
          proofs: Array.isArray(p.refundProofUrls) ? p.refundProofUrls : [] });
      }
    }
    for (const i of (invoices ?? [])) {
      ev.push({
        at: i.invoiceDate, color: i.status === 'CANCELLED' ? 'red' : 'blue', icon: <FileCheck size={16} />,
        title: i.status === 'CANCELLED'
          ? <span>Tax invoice <b>{i.invoiceNo}</b> voided</span>
          : <span>Tax invoice <b>{i.invoiceNo}</b> raised · {inr2(i.grandTotal)}</span>,
      });
    }
    return ev.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [timeline, orderPayments, invoices]);

  const deliverShipment = useMutation({
    mutationFn: ({ sid, podUrl }: { sid: string; podUrl?: string }) =>
      request({ url: `/orders/${id}/shipments/${sid}/deliver`, method: 'POST', data: { podUrl } }),
    onSuccess: () => {
      message.success('Marked delivered');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // Dispatch (release): flip the packed shipment(s) to DISPATCHED. No batch/stock work — that
  // happened at pack. Logistics were captured in the pack modal; this just sends the goods.
  const dispatch = useMutation({
    mutationFn: () => request({ url: `/orders/${id}/shipments/dispatch`, method: 'POST', data: {} }),
    onSuccess: () => {
      message.success('Order dispatched');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Dispatch failed'),
  });

  const reorder = useMutation({
    mutationFn: () => request<any>({ url: `/orders/from/${id}`, method: 'POST' }),
    onSuccess: (newOrder: any) => {
      message.success(`Reorder ${newOrder.orderNo} placed`);
      navigate(`/orders/${newOrder.id}`);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reorder failed'),
  });

  const cancelShipment = useMutation({
    mutationFn: ({ sid, reason }: { sid: string; reason: string }) =>
      request({ url: `/orders/${id}/shipments/${sid}/cancel`, method: 'POST', data: { reason } }),
    onSuccess: () => {
      message.success('Shipment cancelled, stock returned');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const askDeliver = (sid: string) => {
    let pod = '';
    Modal.confirm({
      title: 'Mark this shipment delivered?',
      width: 480,
      content: <PodPicker onChange={(url) => { pod = url; }} />,
      // Name the act on the button, like every other confirm here ("Void invoice", "Pack 28 units").
      // A bare "OK" under a POD uploader reads as "dismiss", not "record the delivery".
      okText: 'Mark delivered',
      onOk: () => deliverShipment.mutate({ sid, podUrl: pod || undefined }),
    });
  };

  const askCancel = (sid: string) => {
    let reason = '';
    Modal.confirm({
      title: 'Cancel this shipment?',
      content: (
        <div style={{ marginTop: 8 }}>
          <div className="text-xs" style={{ color: token.colorTextSecondary, marginBottom: 6 }}>Stock for these products will be returned. Reason:</div>
          <Input.TextArea rows={2} onChange={(e) => (reason = e.target.value)} />
        </div>
      ),
      okType: 'danger',
      onOk: () => cancelShipment.mutate({ sid, reason: reason || 'Cancelled by user' }),
    });
  };

  // Shipments still awaiting delivery (drives the header "Mark Delivered" action).
  const openShipments = (shipments ?? []).filter((s: any) => s.status === 'DISPATCHED');
  const hasPackedShipment = (shipments ?? []).some((s: any) => s.status === 'PACKED');
  // PER_SHIPMENT shipments that still need their own Tally invoice (mirrors the per-shipment panel's
  // per-row gate) — drives the header "Upload invoice" shortcut + the pick-which-dispatch modal.
  const shipmentsNeedingInvoice = (shipments ?? []).filter((s: any) =>
    s.status !== 'CANCELLED'
    && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(s.status)
    && !(invoices ?? []).some((i: any) => String(i.shipmentId) === String(s.id) && i.status !== 'CANCELLED'));

  // `?upload=invoice` — the Orders list's "Upload the tax invoice" lands here with the upload already open, under the
  // same gates as the header buttons. Read once and removed from the URL, so a refresh after closing it does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadRequested = searchParams.get('upload') === 'invoice';
  const uploadRequestDone = useRef(false);
  useEffect(() => {
    if (!uploadRequested || uploadRequestDone.current || !o || !invoicesFetched || !shipmentsFetched) return;
    uploadRequestDone.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('upload');
    setSearchParams(next, { replace: true });
    if (!canUploadInvoice) return;
    if (o.invoiceMode === 'PER_SHIPMENT') {
      if (shipmentsNeedingInvoice.length === 1) setInvoiceForShipment(shipmentsNeedingInvoice[0]);
      else if (shipmentsNeedingInvoice.length > 1) setInvoicePickerOpen(true);
      else message.info('No packed shipment on this order is waiting for a tax invoice.');
    } else if (o.invoiceBeforeDispatch && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(o.status)
      && !(invoices ?? []).some((i: any) => i.status !== 'CANCELLED')) {
      setInvoiceUploadOpen(true);
    } else {
      message.info('This order is not waiting for a tax invoice.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadRequested, o, invoicesFetched, shipmentsFetched, invoices, shipments]);
  // Allocated batches per order line, from the order's non-cancelled shipments. batchNo/expiry are
  // present only for internal staff (the API omits them for retailers), so this shows nothing to them.
  const batchesByLine = useMemo(() => {
    const m = new Map<string, { batchNo: string; qty: number; expiry?: string | null }[]>();
    for (const s of (shipments ?? [])) {
      if (s.status === 'CANCELLED') continue;
      for (const it of (s.items ?? [])) {
        if (!it.batchNo) continue;
        const key = String(it.orderItemId);
        const arr = m.get(key) ?? [];
        arr.push({ batchNo: it.batchNo, qty: Number(it.qty), expiry: it.expiry });
        m.set(key, arr);
      }
    }
    return m;
  }, [shipments]);
  const handleMarkDelivered = () => {
    if (openShipments.length === 1) {
      askDeliver(openShipments[0].id);
    } else {
      // More than one open shipment — let the user choose which to deliver.
      setActiveTab('docs');
      message.info('This order has multiple shipments — mark each one delivered in the Documents tab.');
    }
  };

  const cancelConsolidation = useConfirmedMutation({ title: "Cancel the stock consolidation?", content: "The transfers raised to bring stock in are cancelled. The order stays where it is.", okText: "Yes, cancel it", danger: true }, {
    mutationFn: () => request({ url: `/orders/${id}/consolidate/cancel`, method: 'POST' }),
    onSuccess: () => {
      message.success('Consolidation cancelled — stock returned to source warehouses');
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to cancel consolidation'),
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [backorderOpen, setBackorderOpen] = useState(false);
  const [backorderReason, setBackorderReason] = useState('');
  const change = useConfirmedMutation((v) => {
    const s = typeof v === "string" ? v : v.status;
    // Re-submitting a sent-back order is not "moving it to placed" — and nobody is told about it
    // (no notification fires on this transition), so the generic copy would promise a ping that
    // never comes. Say what actually happens: it rejoins the approval queue as it stands.
    if (s === "PLACED") {
      return {
        title: "Re-submit this order for approval?",
        content: "It goes back into the approval queue exactly as it stands — make sure whatever needed correcting has been settled with the company first.",
        okText: "Yes, re-submit",
      };
    }
    return { title: `Move this order to ${s.replace(/_/g, " ").toLowerCase()}?`, content: "Everyone following this order is told about the change.", okText: "Yes, change it", danger: s === "CANCELLED" };
  }, {
    // `vars` is the target status, optionally carrying a reason. The backend requires a reason for a
    // post-approval cancellation (CANCEL_REASON_REQUIRED), so the cancel modal sends one; every other
    // transition keeps passing a bare status string.
    mutationFn: (vars: string | { status: string; notes?: string }) => {
      const { status, notes } = typeof vars === 'string' ? { status: vars, notes: undefined } : vars;
      return request({ url: `/orders/${id}/status`, method: 'PATCH', data: notes?.trim() ? { status, notes: notes.trim() } : { status } });
    },
    onSuccess: (res: any, vars: string | { status: string; notes?: string }) => {
      const target = typeof vars === 'string' ? vars : vars?.status;
      // A post-approval cancel by anyone who cannot DECIDE one comes back as a request, not a change —
      // saying "Status changed" there would be a lie, and the user would go looking for a cancelled order.
      if (res?.requested) {
        message.success('Cancellation sent to the Super Admin — the order is frozen until they decide');
      } else if (target === 'PLACED') {
        message.success('Re-submitted — the order is back in the approval queue');
      } else {
        message.success('Status changed');
      }
      setCancelModalOpen(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  /*
   * PARK THE ORDER FOR STOCK — through the dedicated endpoint, not a bare status flip.
   *
   * POST /orders/:id/backorder records WHY, writes the BACKORDERED audit action and tells the dealer
   * and their salesperson. Flipping the status generically did none of those: the order simply went
   * quiet, with nothing on the record saying what it was waiting for and nobody outside the office
   * told it was waiting at all.
   */
  const backorder = useMutation({
    mutationFn: (reason: string) => request({ url: `/orders/${id}/backorder`, method: 'POST', data: reason.trim() ? { reason: reason.trim() } : {} }),
    onSuccess: () => {
      message.success('Held for stock — the dealer has been told');
      setBackorderOpen(false);
      setBackorderReason('');
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not hold this order for stock'),
  });

  // Short-close: drop the un-shipped remainder of a partially fulfilled order (Admin/SA). The dispatched
  // part stays; the order total is revised to what was actually fulfilled (advance settled in Accounts).
  const shortClose = useConfirmedMutation({ title: "Short-close this order?", content: "The un-shipped remainder is dropped and the order total is revised to what was actually delivered.", okText: "Yes, short-close", danger: true }, {
    mutationFn: (reason: string) => request({ url: `/orders/${id}/cancel-remainder`, method: 'POST', data: reason ? { reason } : {} }),
    onSuccess: () => {
      message.success('Pending units short-closed — settle any advance excess in Accounts');
      setCancelModalOpen(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Short-close failed'),
  });

  // Void (cancel) a recorded Tally invoice — reverses the ledger so a corrected one
  // can be re-uploaded, or so the order can be cancelled.
  const voidInvoice = useMutation({
    mutationFn: (invId: string) => request({ url: `/invoices/${invId}/void`, method: 'POST', data: {} }),
    onSuccess: () => {
      message.success('Invoice voided');
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-invoices', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to void invoice'),
  });

  // Re-run the proforma-vs-Tally comparison, or (admin) approve a mismatch to unblock dispatch.
  const refreshInv = () => {
    qc.invalidateQueries({ queryKey: ['order', id] });
    qc.invalidateQueries({ queryKey: ['order-invoices', id] });
  };
  const verifyInvoice = useConfirmedMutation({ title: "Verify this invoice?", content: "It is accepted as matching the order and dispatch can go ahead.", okText: "Yes, verify" }, {
    mutationFn: (invId: string) => request<any>({ url: `/invoices/${invId}/verify`, method: 'POST', data: {} }),
    onSuccess: (r: any) => {
      if (r?.status === 'VERIFIED') message.success('Invoice verified — matches the order');
      else message.warning('Invoice does NOT match the order — approve the mismatch to dispatch');
      refreshInv();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const overrideInvoice = useConfirmedMutation({ title: "Override the invoice check?", content: "Dispatch goes ahead even though the invoice does not match. Your reason is recorded against the order.", okText: "Yes, override", danger: true }, {
    mutationFn: ({ invId, reason }: { invId: string; reason: string }) => request({ url: `/invoices/${invId}/verify-override`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Mismatch approved — dispatch unblocked'); setOverrideFor(null); setOverrideReason(''); refreshInv(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const [proformaBusy, setProformaBusy] = useState(false);
  // Same key as ProformaCard's — react-query dedupes it to one request.
  const { data: proformaStatus } = useQuery({
    queryKey: ['proforma-status', String(id)],
    queryFn: () => request<any>({ url: `/orders/${id}/proforma-status` }),
    enabled: !!id,
  });
  // Same condition as the header Proforma button — a proforma exists for any order that is not a
  // draft and has not been cancelled/rejected.
  const proformaAvailable = canProforma && !['DRAFT', 'CANCELLED', 'REJECTED'].includes(o?.status);
  const [genBusy, setGenBusy] = useState(false);
  // Generate the GST tax invoice in-app from this delivered order, then open its PDF.
  const generateInvoice = async () => {
    setGenBusy(true);
    try {
      const inv = await request<any>({ url: `/invoices/from-order/${id}`, method: 'POST', data: {} });
      message.success(`Invoice ${inv.invoiceNo} generated`);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-invoices', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e: any) {
      message.error(await readDownloadError(e, 'Failed to generate invoice'));
    } finally {
      setGenBusy(false);
    }
  };
  // Open the proforma as a PREVIEW (new tab, native viewer w/ its own download button) rather than
  // force-downloading — the user asked to see it first and download only if they want to.
  const viewProforma = async () => {
    setProformaBusy(true);
    try {
      await previewPdf(() => api.get(`/orders/${id}/proforma`, { responseType: 'blob' }));
    } catch (e: any) {
      message.error(await readDownloadError(e, 'Failed to open proforma'));
    } finally {
      setProformaBusy(false);
    }
  };

  if (isLoading) return <div style={{ padding: 24 }}><Spin tip="Loading…" /></div>;
  if (isError || !o) {
    return (
      <Result
        status="error"
        title="Couldn't load this order"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/orders')}>Back to orders</Button>}
      />
    );
  }

  // New-flow orders are billed before dispatch and go DELIVERED → CLOSED, so the
  // INVOICED stage never applies — drop it from the progress tracker.
  const gated = !!o.invoiceBeforeDispatch;
  const steps = gated ? STEPS.filter((s) => s !== 'INVOICED') : STEPS;
  const stepIdx = steps.indexOf(o.status);
  const totalPending = Object.values(pending ?? {}).reduce((s, v) => s + Number(v || 0), 0);
  // Summary "Discount" = the full reduction from subtotal down to the net taxable value — i.e. a line
  // discount AND a LOCKED advance cash discount (which nets the lines but is not stored in discount_amt).
  // Using subtotal − taxableAmt keeps the cards honest: Subtotal − Discount + Tax == Grand Total. For an
  // ESTIMATED CD the lines are still gross (taxableAmt == subtotal), so this is 0 until the CD is locked.
  const discountTotal = Number(o.taxableAmt) > 0
    ? Math.max(0, Number(o.subtotal ?? 0) - Number(o.taxableAmt ?? 0))
    : Number(o.discountAmt ?? 0);
  const discountPctLabel = Number(o.subtotal) > 0 && discountTotal > 0
    ? ` (${Math.round((discountTotal / Number(o.subtotal)) * 10000) / 100}%)` : '';
  // Effective ordered qty (respecting any short-close) — lets the invoice modal warn when
  // the order is only partially packed so users don't bill the whole order for a partial dispatch.
  const totalOrdered = (o.items ?? []).reduce((s: number, it: any) => s + Math.max(0, Number(it.qty ?? 0) - Number(it.cancelledQty ?? 0)), 0);

  // Invoice / payment readiness — drives both the "Close order" gate and the next-step
  // banner. The backend refuses to close an order while any non-cancelled invoice still
  // carries a balance, so we surface that up front instead of after a failed click.
  const liveInvoices = (invoices ?? []).filter((i: any) => i.status !== 'CANCELLED');
  const hasInvoice = liveInvoices.length > 0;
  // Per-shipment dispatch-invoice gate. dispatch() releases the PACKED shipment(s); in PER_SHIPMENT mode it
  // requires EACH to carry its OWN verified/overridden Tally invoice (shipment.service.dispatch). So an
  // order-level "some invoice is verified" check would wrongly enable Dispatch after only the FIRST shipment
  // was billed, leaving a newly packed, un-invoiced shipment the backend then rejects. Gate on the packed
  // shipments actually about to be released.
  const perShipment = o.invoiceMode === 'PER_SHIPMENT';
  const packedShipments = (shipments ?? []).filter((s: any) => s.status === 'PACKED');
  const shipInv = (s: any) => liveInvoices.filter((i: any) => String(i.shipmentId) === String(s.id));
  const packedNeedingInvoice = packedShipments.filter((s: any) => shipInv(s).length === 0);
  const packedInvoiceUnverified = packedShipments.filter((s: any) =>
    shipInv(s).length > 0 && !shipInv(s).some((i: any) => ['VERIFIED', 'OVERRIDDEN'].includes(i.verificationStatus)));
  // `gated` (defined above for the stepper) marks the new flow: the Tally tax invoice must be uploaded (at
  // PACKED) before dispatch. Legacy orders keep the old flow. PER_SHIPMENT → each packed shipment needs one.
  const needsInvoiceBeforeDispatch = gated && (perShipment ? packedNeedingInvoice.length > 0 : !hasInvoice);
  // Dispatch needs the invoice VERIFIED (or its mismatch admin-overridden) — matching the backend dispatch()
  // gate. Merely having an invoice on file is NOT enough. Legacy (non-gated) orders skip it.
  const dispatchInvoiceReady = !gated
    ? true
    : perShipment
      ? packedNeedingInvoice.length === 0 && packedInvoiceUnverified.length === 0
      : liveInvoices.some((i: any) => ['VERIFIED', 'OVERRIDDEN'].includes(i.verificationStatus));
  const invBilled = liveInvoices.reduce((s: number, i: any) => s + Number(i.grandTotal ?? 0), 0);
  const invPaid = liveInvoices.reduce((s: number, i: any) => s + Number(i.paidAmt ?? 0), 0);
  const unpaidInvoice = liveInvoices.find((i: any) => Number(i.balanceAmt ?? 0) > 0.01);
  // Order-based due (invoice allocation retired): the payment-summary drives close-readiness + the due
  // display, NOT the invoice balance (payments link to the order now, not the invoice). Falls back to
  // the legacy invoice balance only until the summary resolves.
  const paySummaryReady = paySummary !== undefined;
  const orderDue = paySummary ? Number(paySummary.due) : Number(unpaidInvoice?.balanceAmt ?? 0);
  const dueCleared = paySummary ? !!paySummary.dueCleared : (hasInvoice && !unpaidInvoice);
  const invoiceCleared = dueCleared;
  // How this order was settled — the split behind "paid": applied account credit vs cash/UPI, measured against
  // the net payable, = balance due. Reuses paySummary for the base + balance (canonical figures); splits the
  // paid amount from the per-payment fromCredit flag (APPROVED, non-refunded only) so credit + cash never
  // double-count. Drives the Payments-tab settlement card, mirroring the proforma PDF's PAYMENT SUMMARY block.
  const settledPays = (orderPayments ?? []).filter((p: any) => p.approvalStatus === 'APPROVED' && p.status !== 'REFUNDED');
  const settleCredit = settledPays.filter((p: any) => p.fromCredit).reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const settleCash = settledPays.filter((p: any) => !p.fromCredit).reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  // Distinct payment mode(s) actually used (UPI / NEFT / RTGS / IMPS / Cheque / Cash / Card / …) so the
  // settlement line names the real method(s) instead of hard-coding "Cash / UPI".
  const settleCashModes = [...new Set(settledPays.filter((p: any) => !p.fromCredit).map((p: any) => p.mode).filter(Boolean))];
  const settlePayable = paySummary ? Number(paySummary.grandTotal) : (settleCredit + settleCash + orderDue);
  const invoicesReady = invoices !== undefined; // the /invoices query has resolved
  const canCloseNow = canEdit && paySummaryReady && dueCleared;
  // Cancel confirmation breakdown — how much of the order is packed / dispatched / delivered / pending, so
  // the person cancelling sees what's already gone (dispatched can't be recalled here) and, if partial, is
  // steered to short-closing just the remainder. Computed BEFORE the cancel gate below because whether
  // anything has shipped out is what decides a non-admin's eligibility.
  // Ignore CANCELLED shipments — their qty has already returned to pending (matches the backend's
  // pending/shipped math, which filters `s.status <> 'CANCELLED'`).
  const liveShipmentRows = (shipments ?? []).filter((s: any) => s.status !== 'CANCELLED');
  const shipQtyByStatus = (st: string) => liveShipmentRows
    .filter((s: any) => s.status === st)
    .reduce((sum: number, s: any) => sum + (s.items ?? []).reduce((a: number, it: any) => a + Number(it.qty || 0), 0), 0);
  const cancelBreakdown: CancelBreakdown = {
    ordered: totalOrdered,
    packed: shipQtyByStatus('PACKED'),
    dispatched: shipQtyByStatus('DISPATCHED'),
    delivered: shipQtyByStatus('DELIVERED'),
    pending: totalPending,
  };
  const hasShippedOut = cancelBreakdown.dispatched + cancelBreakdown.delivered > 1e-6;
  /*
   * WHICH PAYMENT ROWS CAN ACTUALLY BE ACTED ON.
   *
   * An advance is refundable once its order is cancelled/rejected (and the payment itself was not
   * rejected); a submitted payment is reviewable while it is PENDING. Computed here rather than
   * inside the column so the table can decide whether the actions column is worth a column at all.
   */
  const canApprovePayment = hasPerm('PAYMENT.APPROVE');
  const paymentRefundable = (p: any) => p.status !== 'REFUNDED'
    && ['CANCELLED', 'REJECTED'].includes(o.status) && p.approvalStatus !== 'REJECTED';
  const paymentRowActions = (orderPayments ?? []).filter((p: any) =>
    (p.approvalStatus === 'PENDING' && canApprovePayment) || (canRefund && paymentRefundable(p)));
  /*
   * SENT BACK FOR CORRECTION — the way back into the queue.
   *
   * "Send back" hands the order to whoever raised it, and CORRECTION_REQUIRED can only go on to
   * PLACED (the state machine forbids CORRECTION_REQUIRED → APPROVED, so even the approver has to
   * re-queue it first). The screen offered no control for that transition at all: the only button on
   * a sent-back order was Cancel, so an order corrected over the phone had to be cancelled and
   * re-keyed under a new number, or moved by a Super Admin through the revert path — which is built
   * to OVERTURN an approver and stamps a permanent reason against them.
   *
   * Gate mirrors the backend exactly (orders controller, PATCH :id/status): an approver/editor may
   * re-queue it, and so may the person it was returned to — the raiser (creator or the sales person
   * it is attributed to) holding ORDER.CREATE. No note is asked for: what needed correcting is
   * settled with the company directly, and this button only says "it is ready to be looked at again".
   */
  const isRaiser = !!myId
    && (String(o.createdBy ?? '') === String(myId) || String(o.salesPersonId ?? '') === String(myId));
  const canResubmit = o.status === 'CORRECTION_REQUIRED' && (canChangeStatus || (isRaiser && canCreate));
  // Cancel: a retailer / sales person / sales head can withdraw an order only BEFORE anything leaves the
  // warehouse; an admin (ORDER.EDIT) can cancel anytime. During a partial dispatch the order still shows
  // PACKED even though earlier shipments are already DISPATCHED/DELIVERED — so gate on hasShippedOut, not
  // the order status alone, or the retailer sees a dead-end "Cancel" on goods that have already gone. The
  // un-shipped remainder is handled separately by short-close. (DELIVERED/INVOICED/CLOSED can't cancel anyway.)
  const canCancelNow = canCancel && (canEdit || (!['DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED'].includes(o.status) && !hasShippedOut));
  // An order may be fully CANCELLED while NOTHING HAS LEFT THE BUILDING — pre-approval, and (since DEF-007)
  // also APPROVED / ACCEPTED / PACKED. Cancelling a PACKED order returns the stock committed at pack. Once a
  // shipment is DISPATCHED or DELIVERED there is no cancel: the remainder is short-closed and what has gone
  // out is corrected through Returns. Mirrors the backend STATE_TRANSITIONS exactly.
  // BACKORDER is in this list for the same reason APPROVED and ACCEPTED are: nothing has been packed,
  // so the cancel is a clean reversal. It is a parked order, not a dead one — a dealer may still
  // withdraw it, and the server now accepts that transition.
  const cancellableStatus = ['DRAFT', 'PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED', 'APPROVED', 'ACCEPTED', 'BACKORDER', 'PACKED'].includes(o.status);
  // Full withdrawal: a cancellable status AND nothing dispatched/delivered AND no live Tally invoice (the
  // backend refuses with LIVE_INVOICE_EXISTS — it must be voided in Billing first).
  // Short-close covers the other case: part already gone, only the pending remainder is dropped.
  const canFullCancelNow = canCancelNow && !hasShippedOut && !hasInvoice && cancellableStatus;
  /*
   * Past approval a cancellation is ASKED for, not done — unless you are the one who decides such a
   * request. Mirrors OrderCancelRequestService.needsApproval on the server; the server is still the
   * authority, this only decides what the button promises.
   */
  /*
   * A cancellation waiting on the Super Admin FREEZES the order: the server refuses pack and dispatch
   * (ORDER_CANCEL_PENDING), so the buttons must refuse too. A live button that 409s is worse than one
   * that explains itself.
   */
  const cancelPending = (o as any).cancelRequest ?? null;
  /*
   * A REJECTION waiting on the Super Admin freezes the order the same way — the server refuses
   * approve / hold / send-back (ORDER_REJECT_PENDING) while one is open, so the page must say so
   * instead of offering buttons that 409.
   */
  const rejectPending = (o as any).rejectRequest ?? null;
  const cancelNeedsApproval = !canDecideCancel
    && ['APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'PACKED', 'DISPATCHED'].includes(o.status);
  const fullCancelBlockedByInvoice = canCancelNow && !hasShippedOut && hasInvoice;
  // Short-close the un-shipped remainder. Allowed for RETAILER/SALES/Admin (ORDER.CANCEL, own orders via the
  // backend scope) when the feature is on and something is pending — EXCEPT when a whole-order invoice also
  // covers the pending (it must be voided / credit-noted first). Per-shipment orders bill each dispatch
  // separately, so the pending units are un-invoiced and drop cleanly (excess advance → advance-in-credit).
  const partialEnabled = !!(o as any).partialFulfillmentEnabled;
  // Mirror every backend cancel-remainder guard so the button only appears when it will actually succeed
  // (no dead-end server error): non-terminal status, no active advance CD, and no whole-order invoice.
  const shortCloseableStatus = !['DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED', 'CANCELLED', 'REJECTED', 'DRAFT'].includes(o.status);
  const advanceCdActive = ['ESTIMATED', 'LOCKED'].includes(o.advanceCdStatus);
  const wholeInvoiceBlocksShortClose = totalPending > 1e-6 && hasInvoice && o.invoiceMode !== 'PER_SHIPMENT';
  const canShortCloseNow = canCancel && partialEnabled && totalPending > 1e-6 && shortCloseableStatus && !advanceCdActive && !wholeInvoiceBlocksShortClose;
  let shortCloseHint: { message: string; description: string } | undefined;
  if (totalPending > 1e-6 && !canShortCloseNow) {
    if (!partialEnabled) {
      shortCloseHint = { message: 'Short-close isn’t enabled here.', description: 'Partial cancellation is turned off in this environment.' };
    } else if (advanceCdActive) {
      // Differentiate: an ESTIMATED CD can be CLEARED (then short-close works); a LOCKED CD cannot be
      // cleared and the discount isn't recomputed on a partial yet — so be honest, not a false "clear it".
      shortCloseHint = o.advanceCdStatus === 'LOCKED'
        ? { message: 'This order’s advance cash discount is locked — the pending units can’t be short-closed here.', description: 'The discount was calculated on the full order; short-closing would change it, and recomputing a cash discount on a partial isn’t supported yet. Fulfil the pending units, or Accounts reconciles them.' }
        : { message: 'Clear the advance cash discount first.', description: 'This order has an advance/prepaid cash discount that isn’t locked yet — clear it in Accounts (the Advance CD panel), then the pending units can be short-closed.' };
    } else if (wholeInvoiceBlocksShortClose) {
      shortCloseHint = { message: 'Void the Tally invoice before short-closing.', description: 'This order is billed as a whole order (all units) — the pending units can’t be dropped until that invoice is voided or credit-noted in Billing (an admin handles this).' };
    } else if (!shortCloseableStatus) {
      shortCloseHint = { message: 'This order can’t be short-closed at its current stage.', description: 'Only an order still being fulfilled (not yet fully dispatched/closed) can have its pending units dropped.' };
    } else if (!canCancel) {
      shortCloseHint = { message: 'Ask an admin to short-close the pending units.', description: 'You don’t have permission to cancel part of this order.' };
    }
  }

  // Timeline tab side panel: who packed + how long each stage took (derived from history timestamps).
  const tl = timeline ?? [];
  const fulfilSteps = tl.length < 2 ? [] : tl.slice(1).map((t: any, i: number) => ({
    // The step the row records — a part-dispatch that left the order PACKED is still the dispatch (DEF-062).
    to: orderTimelineStep(t).step,
    ms: new Date(t.at).getTime() - new Date(tl[i].at).getTime(),
  }));
  const fulfilMaxMs = fulfilSteps.length ? Math.max(...fulfilSteps.map((s) => s.ms)) : 0;
  const fulfilTotalMs = tl.length > 1 ? new Date(tl[tl.length - 1].at).getTime() - new Date(tl[0].at).getTime() : 0;
  const packedByName = tl.find((t: any) => t.toStatus === 'PACKED')?.changedByName ?? null;

  // Plain-language "what to do next" for the current status. The INVOICED step is
  // payment-aware: it states whether dues are cleared and whether the order can close.
  const nextStep: { type: 'info' | 'success' | 'warning'; msg: string; desc: string; showInvoice?: boolean } | null = (() => {
    // The dealer reads where their order is, not the company's next step — every staff line below names a button
    // or a role they don't have ("Approve to reserve stock", "its warehouse manager", "bill this order in Tally").
    if (isCustomer) {
      const stillToPay = paySummaryReady && orderDue > 0.005 ? ` ${inr2(orderDue)} is still to pay on this order.` : '';
      switch (o.status) {
        case 'PLACED': return { type: 'info', msg: 'Order received', desc: 'We are reviewing your order — you will be notified as soon as it is approved.' };
        case 'AWAITING_PRODUCTION':
        case 'IN_PRODUCTION': return { type: 'warning', msg: 'Waiting for stock', desc: 'Some items are short right now. Your order is prepared as soon as stock arrives.' };
        case 'APPROVED': {
          const wh = o.dispatchWarehouseId ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId)) : null;
          return { type: 'info', msg: 'Approved', desc: `Your order is approved and will be prepared${wh ? ` at ${wh}` : ''}.${stillToPay}` };
        }
        case 'ACCEPTED': return { type: 'info', msg: 'Being prepared', desc: `Your order is being picked and packed.${stillToPay}` };
        case 'PACKED': return totalPending > 1e-6 && totalOrdered - totalPending > 1e-6
          ? { type: 'warning', msg: 'Part-packed', desc: `${fmtQty(totalOrdered - totalPending)} of ${fmtQty(totalOrdered)} units are packed or on their way; the rest follows when stock arrives.${stillToPay}` }
          : { type: 'info', msg: 'Packed', desc: `Your order is packed and will be dispatched shortly.${stillToPay}` };
        case 'DISPATCHED': return { type: 'info', msg: 'On the way', desc: `Your order has left the warehouse.${stillToPay}` };
        case 'DELIVERED':
        case 'INVOICED': return stillToPay
          ? { type: 'warning', msg: 'Delivered — payment pending', desc: stillToPay.trim(), showInvoice: false }
          : { type: 'success', msg: 'Delivered', desc: 'Your order is delivered and nothing is outstanding on it.' };
        case 'CLOSED': return { type: 'success', msg: 'Order complete', desc: 'This order is complete. Use Reorder to place the same items again.' };
        default: return null;
      }
    }
    switch (o.status) {
      case 'PLACED': return { type: 'info', msg: 'Awaiting approval', desc: 'Review the order, then Approve to reserve stock — or Cancel.' };
      case 'AWAITING_PRODUCTION': return { type: 'warning', msg: 'Short on stock (legacy)', desc: 'Awaiting stock — resolve with a warehouse transfer or backorder, or cancel the order.' };
      case 'IN_PRODUCTION': return { type: 'warning', msg: 'Short on stock (legacy)', desc: 'Awaiting stock — resolve with a warehouse transfer or backorder, or cancel the order.' };
      case 'APPROVED': return {
        type: 'info', msg: 'Approved',
        // Naming the warehouse here answers "who is accepting this?" without opening the coverage
        // table — the question every reader of an approved order has first.
        desc: (() => {
          const wh = o.dispatchWarehouseId ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId)) : null;
          return wh
            ? `Assigned to ${wh} — its warehouse manager starts fulfillment to take it on.`
            : 'The assigned warehouse manager starts fulfillment to take it on.';
        })(),
      };
      case 'ACCEPTED': return { type: 'info', msg: 'In fulfillment', desc: 'The warehouse has taken this order on and is preparing it — use “Pick & pack” to choose batches; that commits stock and packs the order.' };
      /*
       * A PART-SHIPPED order also sits at PACKED — the flip to DISPATCHED waits for every unit — so
       * the plain "stock is committed, press Dispatch" advice was wrong for it twice over: there may
       * be nothing packed to dispatch, and it hid that the dealer is still owed goods. The balance is
       * called "on backorder", the same word the whole-order state uses.
       */
      case 'PACKED': {
        // Every PACKED message is read off the shipments, the still-to-pack balance and the invoices. Until
        // all three have loaded, a part-shipped order read as "nothing pending, nothing to invoice" and
        // flashed "Packed — Stock is committed, press Dispatch" — so say nothing rather than the wrong thing.
        if (pending === undefined || shipments === undefined || invoices === undefined) return null;
        const shippedSoFar = totalOrdered - totalPending;
        if (totalPending > 1e-6 && shippedSoFar > 1e-6) {
          // Say what actually happened to the units that are no longer pending. "Gone out and billed" was
          // printed for units still sitting PACKED in the warehouse with no invoice — packed is not
          // dispatched, and nothing here knows whether an invoice exists.
          const dispatchedSoFar = cancelBreakdown.dispatched + cancelBreakdown.delivered;
          const awaitingDispatch = cancelBreakdown.packed;
          const split = dispatchedSoFar > 1e-6
            ? `${fmtQty(dispatchedSoFar)} dispatched${awaitingDispatch > 1e-6 ? `, ${fmtQty(awaitingDispatch)} packed and waiting to dispatch` : ''}`
            : 'packed, waiting to dispatch';
          return {
            type: 'warning',
            msg: `${dispatchedSoFar > 1e-6 ? 'Part-shipped' : 'Part-packed'} — ${fmtQty(totalPending)} on backorder`,
            desc: `${fmtQty(shippedSoFar)} of ${fmtQty(totalOrdered)} units ${dispatchedSoFar > 1e-6 && awaitingDispatch > 1e-6 ? `— ${split}` : dispatchedSoFar > 1e-6 ? 'dispatched' : split}. The balance is on backorder: it packs when stock covers it, and you are notified when that happens. Short-close it instead if the dealer no longer wants the rest.`,
          };
        }
        return needsInvoiceBeforeDispatch
        ? { type: 'warning', msg: 'Packed — invoice required before dispatch', desc: 'Accounts/Admin: bill this order in Tally (use the Proforma), then upload the tax invoice with the upload button above. Dispatch unlocks once the invoice is verified.' }
        : !dispatchInvoiceReady
          ? { type: 'warning', msg: 'Packed — verify the invoice before dispatch', desc: 'The Tally tax invoice is uploaded but not yet verified against the order. Verify it (Documents tab); a mismatch needs an admin override. Dispatch unlocks once it’s verified.' }
          : { type: 'info', msg: 'Packed', desc: 'Stock is committed. Release the goods with the “Dispatch” button.' };
      }
      // Only a status-changer (Admin/SA — ORDER.APPROVE/EDIT) can mark delivery. A warehouse manager
      // was being told to "Mark Delivered" from a page that gives them no such button, and their
      // dispatch register has no action either — so the next step is named for whoever is reading.
      case 'DISPATCHED': return canChangeStatus
        ? { type: 'info', msg: 'In transit', desc: 'Mark Delivered once the retailer receives it (attach POD if available).' }
        : { type: 'info', msg: 'In transit', desc: 'The goods have left the warehouse. Admin marks it Delivered once the retailer confirms receipt.' };
      // Payment-aware like INVOICED below: a paid-up order read "Record payment, then Close…" with ₹0 due (E2E 15 Sep).
      case 'DELIVERED': return !gated
        ? { type: 'info', msg: 'Delivered', desc: 'Generate the GST tax invoice for this order.' }
        : !paySummaryReady ? null
          : dueCleared
            ? { type: 'success', msg: 'Delivered and paid in full — ready to close', desc: 'The tax invoice was raised before dispatch and nothing is outstanding. You can now Close the order.' }
            : { type: 'warning', msg: 'Delivered — payment pending', desc: `${inr2(orderDue)} is still outstanding on this order. Record/approve the payment, then Close.` };
      case 'INVOICED':
        if (invoices === undefined) return null; // not loaded yet — "no invoice" would be a guess
        if (!hasInvoice) return { type: 'info', msg: 'Invoiced', desc: 'Close the order once payment is settled.' };
        return dueCleared
          ? { type: 'success', msg: 'Paid in full — ready to close', desc: 'All dues on this order are cleared. You can now Close it.' }
          : { type: 'warning', msg: 'Payment pending — order cannot be closed yet', desc: `${inr2(orderDue)} still outstanding on this order. Record/approve the payment, then Close.`, showInvoice: false };
      case 'CLOSED': return { type: 'success', msg: 'Order closed', desc: 'This order is complete. Use Reorder to place the same items again.' };
      default: return null; // DRAFT, CANCELLED, AWAITING_TRANSFER (which has its own banner)
    }
  })();

  return (
    <div>
      {/* Header — the page's identity band, so it gets the same diagonal brand→harvest wash as the
          dashboard greeting rather than sitting as one more white card among the eight below it.
          The wash is a background-IMAGE over the Card's own background-COLOR, so nothing conflicts. */}
      <Card style={{ marginBottom: 12 }} className="bg-linear-to-br from-brand-500/[0.10] via-transparent via-55% to-harvest-400/[0.11]">
        {/* Stacked on phones/tablets (buttons below the title); on laptops/desktops the actions move
            to the top-right corner (title left, buttons right, both top-aligned). */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Typography.Title level={4} style={{ margin: 0 }}>
              {o.orderNo} <Tag color={STATUS_COLOR[o.status]} style={{ marginLeft: 8 }}>{statusLabel(o.status)}</Tag>
            </Typography.Title>
            {(() => {
              // The retailer name arrives as "NAME (R-code)"; split so the code shows as a subtle chip
              // and the info reads as scannable who / when / how lines instead of a run-on · list.
              const retailerFull = String(o.retailerName || nameOf(retailerName, o.retailerId) || '—');
              const m = retailerFull.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
              const rName = m ? m[1] : retailerFull;
              const rCode = m ? m[2] : null;
              return (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
                  {/* The masthead strip names three different things — WHO the order is for, WHEN it
                      was placed, HOW it came in — and each glyph now carries its own hue so the reader
                      picks the one they want by colour instead of reading all three. Grey-on-grey they
                      were decoration; the marks already VARY by shape, the tint makes that legible. */}
                  <span className="inline-flex items-center gap-2">
                    <Store className="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
                    <RetailerLink id={o.retailerId} className="font-semibold">{rName}</RetailerLink>
                    {rCode && (
                      <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-muted">{rCode}</span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-muted">
                    <Clock className="size-4 text-harvest-600 dark:text-harvest-300" /> Placed {formatIst(o.orderDate)}
                  </span>
                  {/* Just the pill. The masthead used to read [globe] Channel [WEB] — three ways of
                      saying one thing, and the globe was hard-coded so it lied on a WhatsApp order.
                      The coloured pill names the route itself, so the word "Channel" is dead weight
                      here; the Overview tab still labels the field properly in its own grid. */}
                  <ChannelTag channel={o.channel} />
                </div>
              );
            })()}
          </div>
          <div className="shrink-0 lg:flex lg:justify-end">
            <Space wrap>
              {/* Reorder is a retailer/sales action — hide it entirely for roles without ORDER.CREATE
                  (e.g. Warehouse Manager, Accounts) rather than showing a disabled button. */}
              {canCreate && ['DELIVERED', 'INVOICED', 'CLOSED'].includes(o.status) && (
                <Tooltip label="Place a new order with the same items">
                  <Popconfirm
                    title="Place a reorder?"
                    description="Copies all items into a fresh draft. Prices and taxes are recalculated."
                    onConfirm={() => reorder.mutate()}
                  >
                    <Button icon={<Repeat size={16} />} loading={reorder.isPending}>
                      Reorder
                    </Button>
                  </Popconfirm>
                </Tooltip>
              )}
              {/* New flow: the Tally tax invoice must be uploaded (while PACKED) before
                  dispatch. Shown to invoice creators until an invoice is on file — also
                  reappears at DISPATCHED/DELIVERED if the invoice was voided (correction). */}
              {gated && o.invoiceMode !== 'PER_SHIPMENT' && !hasInvoice && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(o.status) && canUploadInvoice && (
                <GatedButton
                  allowed={canUploadInvoice}
                  reason="You don't have permission to upload the Tally invoice (requires INVOICE.CREATE or warehouse pack rights)"
                  variant="primary"
                  icon={<Upload size={16} />}
                  onClick={() => setInvoiceUploadOpen(true)}
                >
                  Upload Tax Invoice
                </GatedButton>
              )}
              {/* Per-shipment header shortcut: the upload also lives in the panel below, but surfacing it in
                  the action bar shows how to unblock a greyed-out Dispatch. Auto-targets the one packed
                  shipment awaiting its invoice; a picker handles the rare 2+ case. Reuses the SAME modal. */}
              {o.invoiceMode === 'PER_SHIPMENT' && shipmentsNeedingInvoice.length > 0 && canUploadInvoice && (
                <GatedButton
                  allowed={canUploadInvoice}
                  reason="You don't have permission to upload the Tally invoice (requires INVOICE.CREATE or warehouse pack rights)"
                  variant="primary"
                  icon={<Upload size={16} />}
                  onClick={() => (shipmentsNeedingInvoice.length === 1 ? setInvoiceForShipment(shipmentsNeedingInvoice[0]) : setInvoicePickerOpen(true))}
                >
                  Upload invoice
                </GatedButton>
              )}
              {/* Pick & pack: choose source batches per line → creates the packed shipment and
                  commits stock. Available while anything is still pending to pack. */}
              {['ACCEPTED', 'PACKED'].includes(o.status) && totalPending > 1e-6 && canPack && (
                <GatedButton
                  allowed={canPack && !cancelPending}
                  reason={cancelPending
                    ? 'A cancellation for this order is with the Super Admin — it cannot be packed until they decide.'
                    : "You don't have permission to pack this order (requires ORDER.EDIT or warehouse pack rights)"}
                  variant="primary"
                  icon={<Inbox size={16} />}
                  onClick={() => setDispatchOpen(true)}
                >
                  {o.status === 'PACKED' ? 'Pack more' : 'Pick & pack'}
                </GatedButton>
              )}
              {/* Dispatch (release): flips the packed shipment(s) to DISPATCHED — the goods leave.
                  No stock movement (committed at pack).

                  NOT RENDERED AT ALL until the invoice gate is satisfied. It used to sit here greyed
                  out beside a live "Upload invoice", which is the button the user actually needs at
                  that moment — two primary-looking controls, only one of them real. The status banner
                  above already says what is required and that dispatch unlocks after it, so hiding
                  loses no explanation. The backend refuses this anyway (INVOICE_REQUIRED /
                  INVOICE_UNVERIFIED in shipment.service.dispatch) — this is presentation over a real
                  gate, never instead of one. Legacy non-gated orders have dispatchInvoiceReady=true. */}
              {hasPackedShipment && canPack && dispatchInvoiceReady && (() => {
                // A "partial" dispatch = un-shipped qty still remains, so this only releases the packed
                // part and the order stays PACKED. Label + confirm say so (and give the split); a final
                // dispatch (nothing left pending) reads plain "Dispatch".
                const partial = totalPending > 1e-6;
                const shipping = cancelBreakdown.packed; // qty in the packed shipment(s) this release sends
                return (
                  <Popconfirm
                    title={partial ? 'Partial dispatch?' : 'Dispatch this order?'}
                    description={partial
                      ? `You're dispatching ${fmtQty(shipping)} now — ${fmtQty(totalPending)} will stay pending. The packed goods leave the warehouse (stock was committed at pack).`
                      : 'The packed goods leave the warehouse. Stock was already committed at pack.'}
                    okText={partial ? 'Partial dispatch' : 'Dispatch'}
                    disabled={!!cancelPending}
                    onConfirm={() => dispatch.mutate()}
                  >
                    <GatedButton
                      allowed={!cancelPending}
                      reason="A cancellation for this order is with the Super Admin — it cannot be dispatched until they decide."
                      variant="primary"
                      icon={<Send size={16} />}
                      loading={dispatch.isPending}
                    >
                      {partial ? 'Partial dispatch' : 'Dispatch'}
                    </GatedButton>
                  </Popconfirm>
                );
              })()}
              {/* Delivery is recorded per shipment (with optional POD) — a STAFF/field action (ORDER.DELIVER:
                  Sales/Head/Admin). Gated on canDeliver so a retailer never even sees it (no greyed button).
                  This header shortcut delivers the open shipment, or jumps to the Documents tab when there
                  is more than one. Shown whenever a dispatched shipment is awaiting delivery — including a
                  partial order still PACKED (30 shipped, 20 pending), not only a fully DISPATCHED order. */}
              {canDeliver && ['PACKED', 'DISPATCHED'].includes(o.status) && openShipments.length > 0 && (
                <GatedButton
                  allowed={canDeliver}
                  reason="You don't have permission to mark deliveries (requires ORDER.DELIVER)"
                  variant="primary"
                  icon={<Flag size={16} />}
                  onClick={handleMarkDelivered}
                >
                  Mark Delivered
                </GatedButton>
              )}
              {/* Sent back for correction: put it back in the approval queue. Sits beside Cancel, which
                  until now was the ONLY thing a returned order offered — see canResubmit above. */}
              {canResubmit && (
                <GatedButton
                  allowed={canResubmit}
                  reason="Only the person this order was sent back to (or an approver) can re-submit it"
                  variant="primary"
                  icon={<Undo2 size={16} />}
                  loading={change.isPending}
                  onClick={() => change.mutate('PLACED')}
                >
                  Re-submit for approval
                </GatedButton>
              )}
              {/* Held for stock consolidation — admin can cancel (in-transit stock returns to source). */}
              {o.status === 'AWAITING_TRANSFER' && (
                <GatedButton
                  allowed={canEdit}
                  reason="You don't have permission to manage this order (requires ORDER.EDIT)"
                  danger
                  icon={<ArrowLeftRight size={16} />}
                  loading={cancelConsolidation.isPending}
                  onClick={() => cancelConsolidation.mutate()}
                >
                  Cancel consolidation
                </GatedButton>
              )}
              {(NEXT[o.status] ?? [])
                // AWAITING_PRODUCTION / IN_PRODUCTION, once production refills stock: re-approve
                // only if it was never approved; if it was already approved, pack directly. While
                // still short, neither Approve nor Pack shows (it self-heals when stock lands).
                .filter((s) => !((o.status === 'AWAITING_PRODUCTION' || o.status === 'IN_PRODUCTION') && s === 'APPROVED' && (o.stockComplete === false || o.everApproved)))
                .filter((s) => !((o.status === 'AWAITING_PRODUCTION' || o.status === 'IN_PRODUCTION') && s === 'PACKED' && (o.stockComplete === false || !o.everApproved)))
                // Dispatch is done via the shipment modal (it allocates stock),
                // not a bare status flip — so hide the raw DISPATCHED button.
                .filter((s) => !(o.status === 'PACKED' && s === 'DISPATCHED'))
                // Delivery is recorded per shipment (Documents tab, with POD);
                // the order auto-completes — so hide the order-level Delivered flip.
                .filter((s) => !(o.status === 'DISPATCHED' && s === 'DELIVERED'))
                // Invoicing is done via "Generate Invoice" below, not a flip.
                .filter((s) => !(o.status === 'DELIVERED' && s === 'INVOICED'))
                // New-flow orders are already invoiced before dispatch, so they can close
                // straight from DELIVERED. Legacy orders must invoice first — hide it there.
                .filter((s) => !(o.status === 'DELIVERED' && s === 'CLOSED' && !gated))
                // Backorder is a stock decision: parking an order that IS fully covered would say
                // something untrue about why it is waiting, so the button appears only while short.
                .filter((s) => s !== 'BACKORDER' || o.stockComplete === false)
                // Approving and holding for stock are the approver's decisions — both endpoints require
                // ORDER.APPROVE. Everyone else (the dealer on their own order, the warehouse manager on the
                // order routed to them) was shown a greyed "Approve" / "Hold for stock" they could never
                // press, which reads as the order waiting on THEM. So they are not offered at all.
                .filter((s) => (s !== 'APPROVED' && s !== 'BACKORDER') || hasPerm('ORDER.APPROVE'))
                // Same rule for the dealer: fulfilment, packing and closing are the company's steps, so a greyed
                // "Start fulfillment" on their own order only reads as the order waiting on them (E2E 15 Sep).
                .filter((s) => !isCustomer || s === 'CANCELLED')
                // Cancel / short-close button: show ONLY when a full cancel is valid (pre-approval) or a
                // short-close is valid. Post-approval with nothing to short-close → hidden (no cancel there).
                .filter((s) => s !== 'CANCELLED' || canFullCancelNow || canShortCloseNow)
                .map((s) =>
                  s === 'BACKORDER' ? (
                    // Parking an order is a decision the dealer is told about and the record has to keep,
                    // so it asks WHY first — same shape as hold / send-back on the approval queue — and
                    // posts to the dedicated endpoint rather than flipping the status.
                    <GatedButton
                      key={s}
                      allowed={!rejectPending && canChangeStatus}
                      reason={rejectPending
                        ? 'A rejection of this order is waiting for the Super Admin — the order is frozen until they decide.'
                        : "You don't have permission to change this order's status"}
                      icon={STATUS_ICON[s]}
                      onClick={() => setBackorderOpen(true)}
                    >
                      {ACTION_LABEL[s] ?? s}
                    </GatedButton>
                  ) : s === 'CANCELLED' ? (
                    // Opens a breakdown-aware confirmation: full withdrawal ONLY before approval, else short-close
                    // of just the pending remainder. A post-approval order can never be fully cancelled.
                    <GatedButton
                      key={s}
                      allowed={!rejectPending && (canFullCancelNow || canShortCloseNow)}
                      reason={rejectPending
                        ? 'A rejection of this order is waiting for the Super Admin — the order is frozen until they decide.'
                        : !canCancel
                        ? "You don't have permission to cancel this order"
                        : hasInvoice
                          ? 'A tax invoice is still live on this order — void or credit-note it in Billing first, then cancel'
                          : hasShippedOut
                            ? 'Part of this order has already been dispatched — cancel only the un-shipped remainder, or use Returns for what has gone out'
                            : 'This order can no longer be cancelled — use Returns or a credit note to correct it'}
                      danger
                      icon={STATUS_ICON[s]}
                      onClick={() => setCancelModalOpen(true)}
                    >
                      {ACTION_LABEL[s] ?? s}
                    </GatedButton>
                  ) : (
                    <GatedButton
                      key={s}
                      // A rejection with the Super Admin freezes EVERY status move: the server refuses
                      // them all (ORDER_REJECT_PENDING), so no forward button may look live.
                      allowed={!rejectPending && (s === 'CLOSED' ? canCloseNow : (s === 'PACKED' ? canPack : (s === 'ACCEPTED' ? canAccept : canChangeStatus)))}
                      reason={
                        rejectPending
                          ? 'A rejection of this order is waiting for the Super Admin — the order is frozen until they decide.'
                          : s === 'CLOSED'
                          ? (!canEdit
                              ? 'Only Admin can close an order (requires ORDER.EDIT) — a Sales Head can approve only.'
                              : !paySummaryReady
                                ? 'Checking payment status…'
                                : `Clear the ${inr2(orderDue)} still outstanding on this order first.`)
                          : s === 'PACKED'
                            ? 'Packing requires ORDER.EDIT or warehouse pack rights (Admin or the assigned Warehouse Manager).'
                            : s === 'ACCEPTED'
                              ? 'Starting fulfillment requires warehouse pack rights (the assigned Warehouse Manager or an Admin).'
                              : "You don't have permission to change this order's status (requires ORDER.APPROVE or ORDER.EDIT)"
                      }
                      variant="primary"
                      icon={STATUS_ICON[s]}
                      onClick={() => (s === 'APPROVED' ? setApproveOpen(true) : s === 'PACKED' ? setDispatchOpen(true) : change.mutate(s))}
                      loading={change.isPending && change.variables === s}
                    >
                      {ACTION_LABEL[s] ?? s}
                    </GatedButton>
                  ),
                )}
              {/* Legacy flow only: in-app invoice generation after delivery. New-flow
                  orders are already invoiced (uploaded at PACKED), so this is hidden. */}
              {o.status === 'DELIVERED' && !gated && !hasInvoice && (
                <GatedButton
                  allowed={canInvoice}
                  reason="You don't have permission to create invoices (requires INVOICE.CREATE)"
                  variant="primary"
                  icon={<FileCheck size={16} />}
                  loading={genBusy}
                  onClick={generateInvoice}
                >
                  Generate Invoice
                </GatedButton>
              )}
              {/* A quotation cannot take money. The card below already says "No proforma yet — nothing
                  to pay against", yet this button offered to collect anyway, and the server accepted:
                  ₹6,100 of cash reached a ₹6,114 order nobody had approved. The button now states the
                  precondition instead of vanishing — a hidden control teaches the user nothing. */}
              {canPay && paySummary && Number(paySummary.due) > 0.01 && !['DRAFT', 'CANCELLED', 'REJECTED', 'CLOSED'].includes(o.status) && (
                proformaStatus?.enabled && !proformaStatus?.issued ? (
                  <Tooltip label={proformaStatus?.pending ?? 'The proforma is issued when the order is approved and a fulfilment warehouse is chosen — until then this is a quotation.'}>
                    <span>
                      <Button variant="default" icon={<Wallet size={16} />} disabled>
                        Not payable yet
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button variant="primary" icon={<Wallet size={16} />} onClick={() => setPayOpen(true)}>
                    Pay {inr2(paySummary.due)}
                  </Button>
                )
              )}
              {/* Hidden until a proforma exists: before approval there is no document at all, because a
                  page carrying a total and a payment QR is one a dealer can pay against — and paying
                  for an order the company has not committed to is what this flow removes. */}
              {canProforma && !['DRAFT', 'CANCELLED', 'REJECTED'].includes(o.status)
                && !(proformaStatus?.enabled && !proformaStatus?.issued) && (
                <Button icon={<FileText size={16} />} onClick={viewProforma} loading={proformaBusy}>
                  Proforma
                </Button>
              )}
              {/* Raise a return — only once goods have actually shipped out and a tax invoice exists (a return
                  reverses an invoice). A retailer/sales action: hidden entirely for roles without RETURN.CREATE
                  (Warehouse Manager, Accounts) rather than shown disabled. */}
              {RETURNS_LIFECYCLE_ENABLED && canRaiseReturn && hasInvoice && hasShippedOut && !['DRAFT', 'CANCELLED', 'REJECTED'].includes(o.status) && (
                <Button
                  variant="default"
                  icon={<Undo2 size={16} />}
                  onClick={() => setReturnOpen(true)}
                >
                  Raise return
                </Button>
              )}
            </Space>
          </div>
        </div>

        {o.status === 'AWAITING_TRANSFER' && (
          <Alert type="warning" showIcon style={{ marginTop: 12 }}
            message="Consolidating stock across warehouses"
            description={
              <div>
                <div style={{ marginBottom: 6 }}>
                  Held until the inter-warehouse transfers are received. The order returns to its previous
                  state automatically once they land — then dispatch it as a single shipment.
                </div>
                {((o as any).transfers ?? []).map((t: any) => (
                  <div key={t.id} className="text-sm">
                    <b>{t.transferNo}</b> · {t.totalQty} units{' '}
                    <Tag color={t.status === 'RECEIVED' ? 'green' : t.status === 'IN_TRANSIT' ? 'gold' : 'default'}>{t.status}</Tag>
                  </div>
                ))}
              </div>
            } />
        )}

        {/*
          * FIRST banner on a frozen order, above the ordinary next-step advice: while a cancellation is
          * with the Super Admin the "next step" is not what this order needs, and a warehouse manager
          * opening it deserves to know why Pick & pack is refusing before they try it.
          */}
        {cancelPending && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Cancellation requested — this order is frozen"
            description={
              <div>
                <div>
                  {cancelPending.reason ? `Reason given: “${cancelPending.reason}”` : 'No reason was recorded.'}
                </div>
                <div style={{ marginTop: 4 }}>
                  A Super Admin decides it in Approvals. Until then the order cannot be packed or dispatched.
                </div>
              </div>
            }
            action={canDecideCancel ? (
              /*
               * `?lane=` — NOT `?type=`. The approvals hub reads its lane filter from `lane`, so the
               * old link was silently ignored and dropped the reader on "Everything": 93 rows of
               * every kind of approval in the business, with no clue which one this button meant.
               * `focus` then opens THIS order's request — the row id is the cancel request's own id,
               * which is exactly what the queue keys that lane on. Same shape the notification
               * deep-links already use (`/approvals?lane=<key>&focus=<id>`).
               */
              <Button size="sm" onClick={() => navigate(`/approvals?lane=order_cancel_request${cancelPending.id ? `&focus=${cancelPending.id}` : ''}`)}>Decide it</Button>
            ) : undefined}
          />
        )}

        {/*
          * Same reasoning for a REJECTION waiting on the Super Admin, and it deserves its own banner
          * rather than a shared one: what is frozen differs (the approval decision, not the packing),
          * and so does what happens next — approve it and the order is over.
          */}
        {rejectPending && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Rejection requested — this order is frozen"
            description={
              <div>
                <div>
                  {rejectPending.reason ? `Reason given: “${rejectPending.reason}”` : 'No reason was recorded.'}
                </div>
                <div style={{ marginTop: 4 }}>
                  A Super Admin decides it in Approvals. Until then the order cannot be approved, held or sent back — and if they agree, it ends here.
                </div>
              </div>
            }
            action={canDecideReject ? (
              <Button size="sm" onClick={() => navigate(`/approvals?lane=order_reject_request${rejectPending.id ? `&focus=${rejectPending.id}` : ''}`)}>Decide it</Button>
            ) : undefined}
          />
        )}

        {nextStep && (
          <Alert
            type={nextStep.type}
            showIcon
            style={{ marginTop: 12 }}
            message={nextStep.msg}
            description={nextStep.desc}
            action={nextStep.showInvoice && unpaidInvoice && hasPerm('INVOICE.VIEW') ? (
              <Button size="sm" onClick={() => navigate(`/billing/invoices/${unpaidInvoice.id}`)}>View invoice</Button>
            ) : undefined}
          />
        )}

        {stepIdx >= 0 && o.status !== 'CANCELLED' && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Steps
              size="small"
              current={stepIdx}
              items={steps.map((s) => ({
                // The raw code, so the strip read APPROVED → ACCEPTED and gave the reader two
                // past participles to tell apart. Same shared label map as everywhere else.
                title: statusLabel(s),
                icon: STATUS_ICON[s],
                // New flow raises the invoice at PACKED — flag it so the tracker shows
                // where billing happened (instead of a now-removed INVOICED step).
                ...(gated && s === 'PACKED' && hasInvoice ? { description: 'Billed ✓' } : {}),
              }))}
            />
          </>
        )}
      </Card>

      {/*
        * TWO COLUMNS. Everything on this page used to be full width and stacked, so wide tables and
        * one-line facts got the same 1,500px and the reader scrolled past a screen of mostly-empty
        * cards to reach the items. Wide things (the money story, the coverage matrix, the tabs) keep
        * the main column; reference cards that are read once (totals, proforma, tax heads) move to a
        * sticky rail. Nothing is hidden — it is placed.
        */}
      {/* Every card in the rail is money-gated, so a Warehouse Manager has no rail at all — the main
          column then takes the full width instead of leaving a third of the screen empty. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
      <div className={cn('min-w-0 space-y-3', canSeeMoney ? 'lg:col-span-8' : 'lg:col-span-12')}>

      {/* Advance / prepaid cash discount — before-billing CD state + the Accounts/Admin/SA lock action. */}
      {canSeeMoney && <AdvanceCdPanel orderId={id} />}

      {/* Admin stock planning: pick the dispatch warehouse, see warehouse/batch availability + FEFO.
          Gated by ORDER.DISPATCH.PLAN (or ORDER.EDIT) — matching the backend — and the fulfilment window.
          Once an order is fully packed the allocation is done (shown in Items → Allocated batches), so the
          planning aid is hidden; it stays only for a PACKED order with a remaining tail to pack. */}
      {(FULFILLMENT_V2_ENABLED
        // V2 window also covers ACCEPTED — that's when the WHM is preparing to pack and may find the
        // assigned warehouse short (the exact case coverage/transfer-suggestion is for). Assign + Consolidate
        // both already allow ACCEPTED, so the actions stay consistent.
        ? (['APPROVED', 'ACCEPTED', 'BACKORDER', 'AWAITING_TRANSFER'].includes(o.status) || (o.status === 'PACKED' && totalPending > 1e-6))
        : (['APPROVED', 'BACKORDER', 'AWAITING_TRANSFER'].includes(o.status) || (o.status === 'PACKED' && totalPending > 1e-6))) && (
        FULFILLMENT_V2_ENABLED
          // V2: a read-only coverage matrix visible to planners AND view-only approvers (actions gated by canPlan).
          ? ((canPlan || canChangeStatus || canReportShortfall) && (
            <WarehouseCoveragePanel orderId={id} dispatchWarehouseId={o.dispatchWarehouseId} canPlan={canPlan} canAddStock={canAddStock}
              canReportShortfall={canReportShortfall} orderStatus={o.status} transfers={(o as any).transfers} />
          ))
          // Legacy: single-warehouse planning aid, planners only (unchanged behaviour when the flag is off).
          : (canPlan && (
            <StockPlanPanel orderId={id} dispatchWarehouseId={o.dispatchWarehouseId} canEdit={canPlan} />
          ))
      )}

      {/* Per-shipment (partial) invoicing: each dispatch is invoiced separately. Renders ONLY for
          PER_SHIPMENT orders — WHOLE orders keep the order-level "Upload Tax Invoice" button above.

          Each shipment is a BOUNDED CARD with a numbered gutter, per the house line-item pattern.
          It used to be one run-on string — "GBS/2026/09/00022 [DISPATCHED] · TA/2026/09/00022 ·
          ₹1,917 [VERIFIED]" — which put two lookalike document numbers side by side, an unlabelled
          money figure between them, and two tags meaning completely different things (a shipment's
          state and an invoice's check) on the same line. Every fact now sits under its own label,
          and the invoice is visibly a thing attached to the shipment rather than more of the same
          sentence. */}
      {o.invoiceMode === 'PER_SHIPMENT' && (shipments ?? []).filter((s: any) => s.status !== 'CANCELLED').length > 0 && (() => {
        const live = (shipments ?? []).filter((s: any) => s.status !== 'CANCELLED');
        return (
          <Card
            title="Per-shipment tax invoices"
            extra={<Tag>{plural(live.length, 'shipment')}</Tag>}
            style={{ marginBottom: 12 }}
          >
            <p className="mb-3 type-body text-muted">
              Each shipment is billed on its own Tally invoice. A shipment cannot be dispatched until its
              invoice is uploaded and matches.
            </p>
            <div className="space-y-2.5">
              {live.map((s: any, i: number) => {
                const inv = (invoices ?? []).find((x: any) => String(x.shipmentId) === String(s.id) && x.status !== 'CANCELLED');
                const v = invoiceVerify(inv?.verificationStatus);
                const canUploadHere = canUploadInvoice && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(s.status);
                return (
                  <div key={s.id} className="rounded-control border border-line bg-surface-2/50 px-3 py-2.5">
                    {/* ONE ROW. The two-tier version left a wide empty gap beside the shipment number
                        while the invoice fields queued underneath it — the width was there, unused.
                        Each fact keeps its own label (that is what stopped "₹1,917 VERIFIED" reading
                        as a payment); they just sit side by side now. Wraps on a narrow viewport. */}
                    <div className="flex flex-wrap items-start gap-x-8 gap-y-2.5">
                      <span className="flex min-w-0 items-start gap-2.5">
                        <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-md bg-brand-600/10 type-caption-strong text-brand-700 dark:bg-brand-400/10 dark:text-brand-400">
                          {i + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="block type-caption text-subtle">Shipment</span>
                          <span className="block type-body-strong text-content">{s.shipmentNo}</span>
                        </span>
                      </span>

                      {inv ? (
                        <>
                          <span>
                            <span className="block type-caption text-subtle">Tax invoice</span>
                            <span className="block type-body-strong text-content">{inv.invoiceNo}</span>
                          </span>
                          {/* LABELLED. Unlabelled beside a "Pay ₹1,917" button it read as money received. */}
                          <span>
                            <span className="block type-caption text-subtle">Invoice amount</span>
                            <span className="block type-body-strong tabular-nums text-content">{inr2(inv.grandTotal)}</span>
                          </span>
                          <span>
                            <span className="block type-caption text-subtle">Figures check</span>
                            <Tag color={v.color} title={v.hint}>{v.label}</Tag>
                          </span>
                        </>
                      ) : (
                        <span className="flex min-w-0 flex-wrap items-center gap-2 self-center">
                          <span className="type-body text-warning-700 dark:text-harvest-300">
                            No tax invoice yet — this shipment cannot be dispatched.
                          </span>
                          {canUploadHere && (
                            <Button size="sm" variant="primary" icon={<Upload size={16} />} onClick={() => setInvoiceForShipment(s)}>
                              Upload invoice
                            </Button>
                          )}
                        </span>
                      )}

                      {/* The shipment's own lifecycle state, pinned right so it never reads as one of
                          the invoice's fields. */}
                      <Tag className="ml-auto shrink-0 self-center" color={STATUS_COLOR[s.status] ?? 'default'}>{statusLabel(s.status)}</Tag>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}


      {/* TOTALS — one card, invoice-style rows.
          Four full-width Statistic cards spent a whole screen row on four numbers that belong
          together and are read as a column, not as separate headline metrics. Every figure is still
          here, including the prepaid net; it now sits in the rail beside the order instead of pushing
          the content that matters down the page. */}
      {/* Hide the settle-advance banner in the two states the backend hard-rejects it (active advance CD →
          deferred CD-settlement step; CANCELLED/REJECTED → use the payment Refund action instead) — mirrors
          the short-close gating above so the action never dead-ends. */}
      {canSeeMoney && Number(advanceExcess?.excess ?? 0) > 0.01 && !advanceCdActive && !['CANCELLED', 'REJECTED'].includes(o.status) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={<span>Advance‑in‑credit <b>{inr2(advanceExcess.excess)}</b> on this order — the retailer paid more than it was finally billed.</span>}
          description={`Paid ${inr2(advanceExcess.paidNet)} · billed ${inr2(advanceExcess.billed)}. Carried as their credit for the next order — refund it here if they want cash back.`}
          action={canRefund && (o as any).partialFulfillmentEnabled
            ? <Button size="sm" variant="primary" onClick={() => setSettleOpen(true)}>Settle advance</Button>
            : undefined}
        />
      )}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (() => {
                const retailerFull = String(o.retailerName || nameOf(retailerName, o.retailerId) || '—');
                const rm = retailerFull.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                const rName = rm ? rm[1] : retailerFull;
                const rCode = rm ? rm[2] : null;
                const mapHref = o.retailerLat && o.retailerLng
                  ? `https://www.google.com/maps?q=${o.retailerLat},${o.retailerLng}`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.retailerAddress ?? '')}`;
                return (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Left — grouped order details */}
                    <div className={cn('space-y-5', canSeeMoney ? 'lg:col-span-2' : 'lg:col-span-3')}>
                      {/* Customer */}
                      <section>
                        <OSectionHead icon={<Store />} tone="brand" title="Customer" hint="Who this order is for, and where it goes" />
                        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                          <OField label="Retailer">
                            <RetailerLink id={o.retailerId} className="font-medium">{rName}</RetailerLink>
                            {rCode && <span className="ml-2 rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-muted">{rCode}</span>}
                          </OField>
                          <OField label="Phone">
                            {o.retailerMobile ? <a href={`tel:${o.retailerMobile}`} className="text-brand-700 hover:underline dark:text-brand-400">{o.retailerMobile}</a> : undefined}
                          </OField>
                          <OField label="Address" className="sm:col-span-2">
                            {o.retailerAddress ? (
                              <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="inline-flex items-start gap-1.5"><MapPin className="mt-0.5 size-4 shrink-0 text-subtle" /> {o.retailerAddress}</span>
                                {/* An action, so it should look like one. As a bare blue phrase it sat
                                    mid-sentence next to the address and read as part of the text —
                                    nothing said it opened anything. A bordered chip with the
                                    leaving-the-app icon makes the affordance and the destination
                                    obvious at a glance, and matches the chip treatment used elsewhere. */}
                                <a
                                  href={mapHref}
                                  target="_blank"
                                  rel="noopener"
                                  title="Open this address in Google Maps (new tab)"
                                  className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-line bg-surface-2 px-2 py-1 type-caption font-medium text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-500/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-400"
                                >
                                  <MapPin className="size-3.5" /> View map
                                  <ExternalLink className="size-3 opacity-70" />
                                </a>
                              </span>
                            ) : undefined}
                          </OField>
                        </dl>
                      </section>

                      {/* Order details */}
                      <section className="border-t border-line pt-5">
                        <OSectionHead icon={<FileText />} tone="sky" title="Order details" hint="How it was raised, and by whom" />
                        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                          <OField label="Order date">{formatIst(o.orderDate)}</OField>
                          <OField label="Channel"><ChannelTag channel={o.channel} /></OField>
                          <OField label="Placed by"><Person id={o.createdBy} role={o.placedByRole} retailerId={o.retailerId}>{o.placedByName || nameOf(userName, o.createdBy)}</Person></OField>
                          <OField label="Sales person"><Person id={o.salesPersonId} role={o.salesPersonRole}>{o.salesPersonName || nameOf(userName, o.salesPersonId)}</Person></OField>
                          <OField label="Warehouse">
                            {o.dispatchWarehouseId
                              ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId))
                              : <span className="text-subtle">Assigned at packing</span>}
                          </OField>
                          {o.approvedAt && (
                            <OField label="Approved">
                              {/* Two units, each unbreakable: the person (name + role pill) and the
                                  moment. Left as plain inline text, the column wrapped inside the
                                  timestamp and dropped a lone "PM" onto its own line — a time is one
                                  thing to a reader, not four words to be broken up. */}
                              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <Person id={o.approvedBy} role={o.approvedByRole}>{o.approvedByName || nameOf(userName, o.approvedBy)}</Person>
                                <span className="whitespace-nowrap text-subtle">· {formatIst(o.approvedAt)}</span>
                              </span>
                            </OField>
                          )}
                        </dl>
                      </section>

                      {/* Remarks */}
                      {o.remarks && o.remarks !== '—' && (
                        <section className="border-t border-line pt-5">
                          <OSectionHead icon={<MessageSquare />} tone="violet" title="Remarks" />
                          <p className="whitespace-pre-line text-sm text-content">{o.remarks}</p>
                        </section>
                      )}
                    </div>

                    {/* The amount summary that used to sit here now lives in the page rail, where it
                        is visible without opening a tab and cannot drift from the Totals card that was
                        repeating it. */}
                  </div>
                );
              })(),
            },
            {
              key: 'items',
              label: `Items (${o.items?.length ?? 0})`,
              children: (
                <Table
                  rowKey="id"
                  dataSource={o.items ?? []}
                  pagination={false}
                  size="small"
                  scroll={{ x: 900 }}
                  /*
                   * The totals row used to span six columns in one blank cell and drop its numbers
                   * left-aligned, so "10" and the line total floated under whatever column happened
                   * to be nearby while the taxable and GST totals were simply missing. Every money
                   * column now carries its OWN total, right-aligned like the figures above it.
                   */
                  summary={(items) => {
                    const arr = items as any[];
                    const sum = (f: string) => arr.reduce((s, r) => s + Number(r[f] ?? 0), 0);
                    const tax = sum('cgstAmt') + sum('sgstAmt') + sum('igstAmt');
                    return (
                      <Table.Summary.Row className="border-t-2 border-line bg-surface-2/60">
                        <Table.Summary.Cell><span className="type-body-strong text-content">Total</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right"><span className="tabular-nums type-body-strong text-content">{fmtQty(sum('qty'))}</span></Table.Summary.Cell>
                        {canSeeMoney && <Table.Summary.Cell />}
                        {canSeeMoney && <Table.Summary.Cell />}
                        {canSeeMoney && <Table.Summary.Cell align="right"><span className="tabular-nums type-body-strong text-content">{inr2(sum('taxableAmt'))}</span></Table.Summary.Cell>}
                        {canSeeMoney && <Table.Summary.Cell align="right"><span className="tabular-nums type-body-strong text-content">{tax > 0 ? inr2(tax) : <span className="text-subtle">&mdash;</span>}</span></Table.Summary.Cell>}
                        {canSeeMoney && <Table.Summary.Cell align="right"><span className="tabular-nums type-body-strong text-content">{inr2(sum('lineTotal'))}</span></Table.Summary.Cell>}
                      </Table.Summary.Row>
                    );
                  }}
                  columns={[
                    /*
                     * Product carries its own identity: name, then a quiet meta line for pack and HSN.
                     * HSN had a column of its own that is empty on most orders — a whole column of
                     * white space saying nothing — while the name ran together with its pack size.
                     */
                    { title: 'Product', dataIndex: 'productNameSnapshot', width: 300, render: (v: any, r: any, i: number) => {
                      const allocs = batchesByLine.get(String(r.id)) ?? [];
                      const variant = variantText(r.packSize, r.uom, r.variantLabel);
                      return (
                        /*
                         * A LINE NUMBER on every row.
                         *
                         * Four products with near-identical names ("AGN", "AGN LTE") and a batch box
                         * under each read as one continuous column of text — nothing said where a
                         * line began or how many there were, and "the third item" was not something
                         * anybody could point at over the phone. The app already numbers cart lines
                         * this way (`line-items-numbered-cards`); an order's items are the same list
                         * after it is placed. The number sits in the Product cell rather than in a
                         * column of its own because the summary row lays its cells out positionally,
                         * and a new column would silently shift every total one place left.
                         */
                        <div className="flex min-w-0 items-start gap-2.5">
                          <span
                            aria-hidden
                            className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 type-caption-strong tabular-nums text-muted"
                          >
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                          <div className="type-body-strong text-content">{v}</div>
                          {(variant || r.hsnCode) && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 type-caption text-subtle">
                              {variant && <span>{variant}</span>}
                              {variant && r.hsnCode && <span aria-hidden>&middot;</span>}
                              {r.hsnCode && <span className="font-mono">HSN {r.hsnCode}</span>}
                            </div>
                          )}
                          {allocs.length > 0 && (
                            <div className="mt-1.5 rounded-control border border-line/70 bg-surface-2/50 px-2 py-1">
                              <div className="type-caption text-subtle">Allocated batches</div>
                              {/* `bi`, not `i` — the row's line number is `i` in the enclosing scope. */}
                              {allocs.map((a, bi) => (
                                <div key={bi} className="flex items-center gap-2 leading-6">
                                  <span className="font-mono text-xs text-content">{a.batchNo}</span>
                                  <Tag color="blue" style={{ margin: 0 }}>&times;{fmtQty(a.qty)}</Tag>
                                  {a.expiry && <span className="whitespace-nowrap type-caption text-subtle">exp {fmtDateShort(a.expiry)}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                    } },
                    { title: 'Qty', dataIndex: 'qty', align: 'right' as const, width: 140, render: (_: any, r: any) => {
                      const phrase = boxLoosePhrase(r.boxQuantity, r.looseUnitQuantity, r.unitsPerBoxSnapshot);
                      if (phrase) {
                        return (
                          <span className="whitespace-nowrap">
                            <span className="tabular-nums type-body-strong text-content">{phrase}</span>
                            <div className="type-caption text-subtle">= {fmtQty(r.qty)} units</div>
                          </span>
                        );
                      }
                      return <span className="tabular-nums text-content">{fmtQty(Number(r.qty))}</span>;
                    } },
                    ...(canSeeMoney ? [
                    { title: 'Rate', dataIndex: 'unitPrice', align: 'right' as const, width: 100,
                      render: (v: any) => <span className="tabular-nums text-content">{inr(v)}</span> },
                    // A discount of nothing is not a number worth reading: "0.00" on every line was
                    // noise competing with the figures that do matter.
                    { title: 'Disc', dataIndex: 'discountPct', align: 'right' as const, width: 80,
                      render: (v: any) => Number(v) > 0
                        ? <span className="tabular-nums text-content">{Number(v)}%</span>
                        : <span className="text-subtle">&mdash;</span> },
                    { title: 'Taxable', dataIndex: 'taxableAmt', align: 'right' as const, width: 120,
                      render: (v: any) => <span className="tabular-nums text-content">{inr(v)}</span> },
                    /*
                     * ONE GST column instead of three. CGST/SGST and IGST are mutually exclusive — an
                     * intra-state line fills the first two and leaves IGST at zero, an inter-state
                     * line does the reverse — so two of the three columns were always empty. The
                     * split survives on the second line and in the tooltip, where it belongs.
                     */
                    { title: 'GST', key: 'gst', align: 'right' as const, width: 130, render: (_: any, r: any) => {
                      const c = Number(r.cgstAmt ?? 0), sg = Number(r.sgstAmt ?? 0), ig = Number(r.igstAmt ?? 0);
                      const tax = c + sg + ig;
                      if (!(tax > 0)) return <span className="text-subtle">&mdash;</span>;
                      const split = ig > 0
                        ? `IGST${gstRate(ig, r.taxableAmt)}`
                        : `CGST${gstRate(c, r.taxableAmt)} + SGST${gstRate(sg, r.taxableAmt)}`;
                      const exact = ig > 0 ? `IGST ${inr2(ig)}` : `CGST ${inr2(c)} · SGST ${inr2(sg)}`;
                      return (
                        <Tooltip label={exact}>
                          <span className="inline-block cursor-help whitespace-nowrap">
                            <span className="tabular-nums text-content">{inr(tax)}</span>
                            <div className="type-caption text-subtle">{split}</div>
                          </span>
                        </Tooltip>
                      );
                    } },
                    { title: 'Amount', dataIndex: 'lineTotal', align: 'right' as const, width: 130,
                      render: (v: any) => <span className="tabular-nums type-body-strong text-content">{inr2(v)}</span> },
                    ] : []),
                  ]}
                />
              ),
            },
            {
              key: 'timeline',
              label: 'Timeline',
              children: !lifecycleEvents.length ? (
                <Empty description="No activity yet" />
              ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  {/* Left — snapshot + timing */}
                  <div className="space-y-5 lg:col-span-1">
                    <div className="rounded-card border border-line bg-surface-2 p-4">
                      <h4 className="mb-3 type-overline text-muted">At a glance</h4>
                      <dl className="space-y-3">
                        <OField label="Retailer"><RetailerLink id={o.retailerId}>{o.retailerName || nameOf(retailerName, o.retailerId)}</RetailerLink></OField>
                        <OField label="Sales person"><Person role={o.salesPersonRole}>{o.salesPersonName || nameOf(userName, o.salesPersonId)}</Person></OField>
                        {/* Keyed off the NAME, not the staff id: a customer's payload carries the
                            approving COMPANY and deliberately no internal id, and gating on the id
                            blanked a row whose value was sitting right there. */}
                        <OField label="Approved by">{o.approvedByName || (o.approvedBy ? nameOf(userName, o.approvedBy) : undefined)
                          ? <Person role={o.approvedByRole}>{o.approvedByName || nameOf(userName, o.approvedBy)}</Person> : undefined}</OField>
                        <OField label="Warehouse">{o.dispatchWarehouseId ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId)) : undefined}</OField>
                        {!isCustomer && <OField label="Packed by">{packedByName ?? undefined}</OField>}
                        <OField label="Payment">{!dueCleared && orderDue > 0.01 ? <Tag color="red">{inr2(orderDue)} due</Tag> : <Tag color="green">Paid</Tag>}</OField>
                        <OField label="Invoice">{liveInvoices.length ? <span className="inline-flex flex-wrap items-center gap-1.5">{liveInvoices[0].invoiceNo}{liveInvoices[0].verificationStatus && <Tag color={invoiceVerify(liveInvoices[0].verificationStatus).color} title={invoiceVerify(liveInvoices[0].verificationStatus).hint}>{invoiceVerify(liveInvoices[0].verificationStatus).label}</Tag>}</span> : undefined}</OField>
                      </dl>
                    </div>
                    {fulfilSteps.length > 0 && !isCustomer && (
                      <div className="rounded-card border border-line bg-surface-2 p-4">
                        <h4 className="mb-1 type-overline text-muted">Fulfilment timing</h4>
                        <div className="divide-y divide-line">
                          {fulfilSteps.map((s, i) => (
                            <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                              <span className="inline-flex items-center gap-2 text-muted">
                                {statusLabel(s.to)}
                                {s.ms === fulfilMaxMs && fulfilMaxMs > 0 && <Tag color="orange">slowest</Tag>}
                              </span>
                              <span className="tabular-nums text-content">{fmtDuration(s.ms)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center justify-between border-t-2 border-line pt-2">
                          <span className="type-body-strong text-content">Total turnaround</span>
                          <span className="type-section-title tabular-nums text-content">{fmtDuration(fulfilTotalMs)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right — activity timeline */}
                  <div className="lg:col-span-2">
                    <h4 className="mb-4 type-overline text-muted">Activity</h4>
                    <Timeline
                      items={lifecycleEvents.map((e) => ({
                        color: e.color,
                        dot: e.icon,
                        children: (
                          <div className="pb-3">
                            {/* The proof rides ON the entry line, right after the timestamp: below it
                                cost a row per event, and right-aligned across this wide column it ended
                                up far from the line it evidences. */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="text-sm text-content">{e.title}</span>
                              <span className="text-2xs text-subtle">{formatIst(e.at)}</span>
                              {!!e.proofs?.length && (
                                <AttachmentCell files={e.proofs} label="Payment proof" size={30} />
                              )}
                            </div>
                            {e.who && (
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                                <User className="size-3.5 shrink-0" />
                                <span>{e.who}</span>
                                {e.whoRole && <RoleBadge role={e.whoRole} />}
                              </div>
                            )}
                            {e.note && (
                              <div className={cn('mt-1 text-xs', e.noteStrong ? 'font-medium text-content' : 'text-muted')}>{e.note}</div>
                            )}
                          </div>
                        ),
                      }))}
                    />
                  </div>
                </div>
              ),
            },
            ...(canSeeMoney ? [{
              key: 'payments',
              label: `Payments (${orderPayments?.length ?? 0})`,
              children: !orderPayments?.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payments submitted for this order yet." />
              ) : (
                <>
                  {/* Settlement summary — how the order's due was cleared: account credit + cash/UPI = balance.
                      Mirrors the proforma PDF's PAYMENT SUMMARY block so screen + document tell the same story. */}
                  <div className="mb-3 rounded-lg border border-line bg-surface-2 p-4">
                    <div className="mb-1.5 flex items-center gap-2">
                      <Wallet size={16} style={{ color: brand.primary }} />
                      <span className="type-body-strong text-content">How this order was settled</span>
                      {dueCleared
                        ? <Tag color="green" style={{ margin: 0 }}>Paid in full</Tag>
                        : <Tag color="orange" style={{ margin: 0 }}>Balance due</Tag>}
                    </div>
                    <AmountRow label="Amount payable" value={inr2(settlePayable)} />
                    {settleCredit > 0.009 && (
                      <AmountRow
                        label={<span className="inline-flex items-center gap-1.5">Less: Account credit applied <Tag color="green" style={{ margin: 0 }}>Credit</Tag></span>}
                        value={`− ${inr2(settleCredit)}`}
                      />
                    )}
                    {settleCash > 0.009 && <AmountRow label={`Less: Paid${settleCashModes.length ? ` — ${settleCashModes.join(', ')}` : ''}`} value={`− ${inr2(settleCash)}`} />}
                    <Divider style={{ margin: '8px 0' }} />
                    <div className="flex items-center justify-between gap-4 py-0.5">
                      <span className="type-body-strong text-content">Balance due</span>
                      <span
                        className={cn('type-metric-sm', orderDue > 0.01 ? 'text-danger-600' : '')}
                        style={orderDue > 0.01 ? undefined : { color: brand.primary }}
                      >
                        {inr2(orderDue)}
                      </span>
                    </div>
                  </div>
                  <Table
                  rowKey="id"
                  size="small"
                  className="[&_table]:table-fixed"
                  dataSource={orderPayments}
                  pagination={false}
                  onRow={(p: any) => {
                    // Clicking the row previews the proof in place. It used to throw the user into a
                    // new tab, which lost the order they were reading.
                    const files: string[] = paymentProofs(p);
                    return files.length
                      ? { onClick: () => setProofView({ files, label: `${p.paymentNo} proof` }), style: { cursor: 'pointer' } }
                      : {};
                  }}
                  columns={[
                    // Mirrors the Payments list page (minus Retailer/Order — redundant on an order). table-fixed +
                    // a width on every column spreads the leftover space EVENLY across all columns (no single big
                    // gap before the last one) while still filling the full table width.
                    { title: 'Payment No', dataIndex: 'paymentNo', width: 150 },
                    { title: 'Date', dataIndex: 'paymentDate', width: 120, render: (v: any) => formatIstDate(v) },
                    { title: 'Mode', dataIndex: 'mode', width: 100, render: (v: string, p: any) => p.fromCredit ? <Tag color="green">Account credit</Tag> : <Tooltip label={p.referenceNo || p.utr || ''}><Tag>{v}</Tag></Tooltip> },
                    {
                      title: 'Received in', key: 'receivedBank', width: 150,
                      render: (_: any, p: any) => p.receivedBank
                        ? <Tooltip label={[p.receivedBank.label, p.receivedBank.bankName, p.receivedBank.last4 ? `A/c ··${p.receivedBank.last4}` : ''].filter(Boolean).join(' · ')}>
                            <span>{(p.receivedBank.bankName || p.receivedBank.label || 'Bank')}{p.receivedBank.last4 ? ` ··${p.receivedBank.last4}` : ''}</span>
                          </Tooltip>
                        : <span className="text-subtle">—</span>,
                    },
                    { title: 'Amount', dataIndex: 'amount', width: 120, render: (v: any) => <b>{inr2(v)}</b> },
                    {
                      title: 'Status', dataIndex: 'approvalStatus', width: 140,
                      render: (s: string, p: any) => {
                        if (p.status === 'REFUNDED') {
                          return (
                            <Tooltip label={`Refunded ${inr2(p.refundAmount ?? p.amount)} via ${p.refundMode ?? ''}${p.refundedByName ? ` by ${p.refundedByName}` : ''}${p.refundReason ? ` — ${p.refundReason}` : ''}`}>
                              <Tag color="purple">Refunded</Tag>
                            </Tooltip>
                          );
                        }
                        const color = s === 'APPROVED' ? 'green' : s === 'REJECTED' ? 'red' : 'gold';
                        const label = s === 'PENDING' ? 'Pending approval' : s === 'APPROVED' ? 'Approved' : 'Rejected';
                        return s === 'REJECTED' && p.rejectedReason
                          ? <Tooltip label={`Reason: ${p.rejectedReason}`}><Tag color={color}>{label}</Tag></Tooltip>
                          : <Tag color={color}>{label}</Tag>;
                      },
                    },
                    {
                      title: 'Submitted by', key: 'submittedBy', width: 180,
                      render: (_: any, p: any) => {
                        const isRetailer = p.submittedByRoleCode === 'RETAILER' || p.submittedByRole === 'Retailer';
                        if (isRetailer) {
                          // Retailer self-submitted → show the RETAILER (company) name, linked to its page —
                          // not the contact-person login name. RetailerLink gates on RETAILER.VIEW.
                          const label = p.retailerName || p.submittedByName;
                          if (!label) return <span className="text-subtle">—</span>;
                          return (
                            <Space size={6}>
                              <RetailerLink id={p.retailerId}>{label}</RetailerLink>
                              <Tag color="green">Retailer</Tag>
                            </Space>
                          );
                        }
                        // Staff (SA / Admin / Accountant / Sales Head / Sales Person…) → their own name, linked
                        // per role + permissions (UserLink gates on USER.VIEW and never links a Super Admin).
                        const name = p.submittedByName || p.recordedByName;
                        if (!name) return <span className="text-subtle">—</span>;
                        // Prefer the role CODE; fall back to the "Super Admin" label so a SA is never linked
                        // even before the backend serves submittedByRoleCode.
                        const roleCode = p.submittedByRoleCode ?? (p.submittedByRole === 'Super Admin' ? 'SUPER_ADMIN' : undefined);
                        return (
                          <Space size={6}>
                            <UserLink role={roleCode}>{name}</UserLink>
                            {p.submittedByRole && <Tag color="blue">{p.submittedByRole}</Tag>}
                          </Space>
                        );
                      },
                    },
                    {
                      title: 'Proof', key: 'proof', align: 'right' as const, width: 110,
                      render: (_: any, p: any) => {
                        // Thumbnails, not a link icon: the reader can tell a UPI screenshot from a
                        // bank slip without opening anything. stopPropagation so a click on the cell
                        // drives the cell's own popover rather than the row's open-first-file.
                        const files = paymentProofs(p);
                        const refunds: string[] = p.status === 'REFUNDED' ? (p.refundProofUrls ?? []) : [];
                        return (
                          <Space size={8} className="justify-end" onClick={(e: any) => e.stopPropagation()}>
                            <AttachmentCell files={files} label={`${p.paymentNo} proof`} />
                            {refunds.length > 0 && <AttachmentCell files={refunds} label={`${p.paymentNo} refund proof`} empty={null} />}
                          </Space>
                        );
                      },
                    },
                    /*
                     * ONE ACTIONS COLUMN, AND ONLY WHEN A ROW CAN ACT.
                     *
                     * This used to be a refund column added for anyone holding PAYMENT.REFUND — an
                     * empty 160px strip on every order that was not cancelled, i.e. almost all of
                     * them. It drew nothing and still counted towards the table's width, which is
                     * why this table scrolled sideways with visibly nothing to scroll to: the
                     * declared widths summed past the container, so `overflow-x-auto` had something
                     * to do. The column now appears only when some row in it has a button.
                     *
                     * Verifying a payment lives here too. A pending collection was visible on this
                     * screen but could only be ACTED on from Billing → Payments, so whoever was
                     * looking at the order had to go and find the same row in another register. It
                     * opens the same review dialog that register opens — same checks, same ledger
                     * post, same invalidations — never a one-click approve, because approving is
                     * meant to happen after looking at the proof and naming the bank it landed in.
                     */
                    ...(paymentRowActions.length ? [{
                      title: '', key: 'rowAct', align: 'right' as const, width: 96,
                      render: (_: any, p: any) => (
                        <Space size={4} className="justify-end" onClick={(e: any) => e.stopPropagation()}>
                          {p.approvalStatus === 'PENDING' && canApprovePayment && (
                            <Tooltip label="Review this payment">
                              <Button size="sm" variant="primary" aria-label={`Review ${p.paymentNo}`} icon={<Eye size={16} />} onClick={() => setPayReviewFor(p)} />
                            </Tooltip>
                          )}
                          {canRefund && paymentRefundable(p) && (
                            <Tooltip label="Process refund">
                              <Button size="sm" danger aria-label={`Process refund for ${p.paymentNo}`} icon={<Repeat size={16} />} onClick={() => setRefundFor(p)} />
                            </Tooltip>
                          )}
                        </Space>
                      ),
                    }] : []),
                  ]}
                />
                </>
              ),
            }] : []),
            {
              key: 'docs',
              // The proforma counts as a document of this order. It used to exist ONLY as a header
              // button, so an order with no shipment and no tax invoice yet reported "Documents (0)"
              // while a proforma was in fact available — which is exactly where a user goes looking
              // for it. Gated identically to that header button so the two can never disagree.
              label: `Documents (${(shipments?.length ?? 0) + (invoices?.length ?? 0) + (proformaAvailable ? 1 : 0)})`,
              children: (
                <>
                  {proformaAvailable && (
                    <DocSection icon={<FileText />} tone="brand" title="Order document">
                      <Table
                        rowKey="key"
                        size="small"
                        pagination={false}
                        // No bottom margin. `DocSection` wraps this in a bordered card, so a margin
                        // inside it parked the table's own last-row rule 16px above the card's
                        // bottom edge and drew the two as a double line. The Shipments and Invoices
                        // tables below have no margin, which is why only this one showed it.
                        dataSource={[{ key: 'proforma' }]}
                        // The whole row opens the document — the trailing icon button was the only
                        // hit target, so clicking the document's NAME did nothing.
                        onRow={() => ({ onClick: viewProforma, style: { cursor: 'pointer' } })}
                        columns={[
                          { title: 'Document', dataIndex: 'key', width: 200, render: () => (
                            <span className="inline-flex items-center gap-2 text-content"><FileText size={16} />Proforma invoice</span>
                          ) },
                          { title: 'Description', key: 'desc', render: () => (
                            <span className="text-muted">Priced summary of this order. Not a tax invoice — the GST invoice appears here once the order is billed.</span>
                          ) },
                          { title: '', key: 'act', width: 120, align: 'right' as const, render: () => (
                            <span onClick={(e: any) => e.stopPropagation()}>
                              <Tooltip label="Open the proforma">
                                <Button size="sm" variant="text" loading={proformaBusy} icon={<FileText size={16} />}
                                  aria-label="Open the proforma" onClick={viewProforma} />
                              </Tooltip>
                            </span>
                          ) },
                        ]}
                      />
                    </DocSection>
                  )}
                  <DocSection icon={<Truck />} tone="sky" title={`Shipments (${shipments?.length ?? 0})`}>
                  {!shipments?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shipments yet — use Pick & pack above to create one." />
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      className="[&_table]:table-fixed"
                      dataSource={shipments}
                      pagination={false}
                      expandable={{
                        expandRowByClick: true,
                        expandedRowRender: (s: any) => (
                          <Table
                            size="small"
                            rowKey="id"
                            dataSource={s.items ?? []}
                            pagination={false}
                            columns={[
                              { title: 'Product', dataIndex: 'productId', render: (v: any) => productName.get(String(v)) ?? `#${v}` },
                              { title: 'Batch', dataIndex: 'batchNo', render: (v: any, r: any) => v
                                ? <><span style={{ fontFamily: 'monospace' }}>{v}</span>{r.expiry ? <span style={{ color: 'var(--gb-text-muted)' }}> · exp {fmtDateShort(r.expiry)}</span> : null}</>
                                : '—' },
                              { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => fmtQty(v) },
                            ]}
                          />
                        ),
                      }}
                      columns={[
                        { title: 'Shipment', dataIndex: 'shipmentNo', width: 160 },
                        {
                          title: 'Status', dataIndex: 'status', width: 120,
                          render: (v: string) => <Tag color={SHIPMENT_COLOR[v]}>{v}</Tag>,
                        },
                        { title: 'Carrier', dataIndex: 'carrier', width: 140, render: (v: any) => v ?? '—' },
                        { title: 'LR', dataIndex: 'lrNo', width: 130, render: (v: any) => <span className="whitespace-nowrap">{v ?? '—'}</span> },
                        { title: 'Vehicle', dataIndex: 'vehicleNo', width: 140, render: (v: any) => <span className="whitespace-nowrap">{v ?? '—'}</span> },
                        {
                          title: 'Dispatched', dataIndex: 'dispatchedAt', width: 180,
                          render: (v: any) => <span className="whitespace-nowrap">{formatIst(v)}</span>,
                        },
                        {
                          title: 'Delivered', dataIndex: 'deliveredAt', width: 180,
                          render: (v: any) => <span className="whitespace-nowrap">{formatIst(v)}</span>,
                        },
                        {
                          title: 'POD', dataIndex: 'podUrl', width: 90, align: 'center' as const,
                          // Same in-page preview as a payment proof — a POD is read the same way.
                          render: (v: any, s: any) => (
                            <span className="inline-flex justify-center" onClick={(e: any) => e.stopPropagation()}>
                              <AttachmentCell files={v ? [v] : []} label={`${s.shipmentNo ?? 'Shipment'} delivery proof`} />
                            </span>
                          ),
                        },
                        {
                          title: '', key: 'actions', width: 180,
                          render: (_: any, s: any) => (
                            <Space wrap onClick={(e: any) => e.stopPropagation()}>
              {/* Delivery is a staff/field action — hidden entirely for a retailer (no greyed button). */}
                              {s.status === 'DISPATCHED' && canDeliver && (
                                <Tooltip label="Mark delivered + record POD">
                                  <Button size="sm" variant="text" icon={<Flag size={16} />} onClick={() => askDeliver(s.id)}>Delivered</Button>
                                </Tooltip>
                              )}
                              {s.status !== 'DELIVERED' && s.status !== 'CANCELLED' && (
                                canEdit ? (
                                  <Popconfirm
                                    title="Cancel and return stock?"
                                    onConfirm={() => askCancel(s.id)}
                                  >
                                    <Button size="sm" variant="text" icon={<XCircle size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Cancel shipment" />
                                  </Popconfirm>
                                ) : (
                                  <GatedButton
                                    allowed={false}
                                    reason="You don't have permission to cancel shipments (requires ORDER.EDIT)"
                                    size="sm"
                                    variant="text"
                                    icon={<XCircle size={16} className="text-danger-600 dark:text-danger-500" />}
                                    aria-label="Cancel shipment"
                                  />
                                )
                              )}
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                  </DocSection>
                  <DocSection icon={<IndianRupee />} tone="gold" title={`Invoices (${invoices?.length ?? 0})`}>
                  {!invoices?.length ? (
                    <Empty description="No invoices linked to this order yet" />
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      className="[&_table]:table-fixed"
                      dataSource={invoices}
                      pagination={false}
                      onRow={(r: any) => ({ onClick: () => navigate(`/billing/invoices/${r.id}`), style: { cursor: 'pointer' } })}
                      columns={[
                        { title: 'Invoice No', dataIndex: 'invoiceNo', width: 160 },
                        {
                          /*
                           * The Tally PDF itself — the document this whole row is about.
                           *
                           * It was reachable from nowhere on this table even though the upload makes
                           * it mandatory, so every invoice here HAS one. It gets its own column
                           * rather than a slot in Actions for two reasons: a document is not an
                           * action, and Actions only renders for someone holding void / verify /
                           * override rights — which is why a Warehouse Manager looking at a VERIFIED
                           * invoice saw an Actions header over an empty cell. Anyone who can see the
                           * invoice can now open its PDF.
                           *
                           * Second column, not last: the table is wider than the pane it sits in, so
                           * a column at the far right starts life past the horizontal scroll — the
                           * document belongs beside the number that names it anyway.
                           */
                          title: 'PDF', key: 'pdf', align: 'center' as const, width: 64,
                          render: (_: any, inv: any) => (
                            <span className="inline-flex" onClick={(e: any) => e.stopPropagation()}>
                              <AttachmentCell files={[inv.pdfUrl].filter(Boolean)} label={`Tally invoice ${inv.invoiceNo}`} />
                            </span>
                          ),
                        },
                        // Human dates, like the payments table above — these printed "2026-09-15" (E2E 15 Sep).
                        { title: 'Date', dataIndex: 'invoiceDate', width: 120, render: (v: any) => <span className="whitespace-nowrap">{formatIstDate(v)}</span> },
                        { title: 'Due', dataIndex: 'dueDate', width: 120, render: (v: any) => <span className="whitespace-nowrap">{formatIstDate(v)}</span> },
                        { title: 'Total', dataIndex: 'grandTotal', width: 120, render: inr2 },
                        { title: 'Paid', dataIndex: 'paidAmt', width: 120, render: inr2 },
                        {
                          title: 'Balance', dataIndex: 'balanceAmt', width: 120,
                          render: (v: any) => <b style={{ color: Number(v) > 0 ? PALETTE.danger[500] : PALETTE.success[500] }}>{inr2(v)}</b>,
                        },
                        { title: 'Status', dataIndex: 'status', width: 110, render: (s: string) => <Tag>{s}</Tag> },
                        {
                          title: 'Verification', dataIndex: 'verificationStatus', width: 150,
                          render: (v: string) => {
                            const m = invoiceVerify(v);
                            return <Tag color={m.color} title={m.hint}>{m.label}</Tag>;
                          },
                        },
                        ...((canVoidInvoice || canVerify || canOverride) ? [{
                          title: 'Actions', key: 'act', align: 'right' as const, width: 120,
                          render: (_: any, inv: any) => {
                            if (inv.status === 'CANCELLED') return <Tag>Voided</Tag>;
                            return (
                              <Space wrap onClick={(e: any) => e.stopPropagation()}>
                                {/* Only Tally-uploaded invoices (with a PDF) are gated; verify re-checks vs the order. */}
                                {canVerify && inv.pdfUrl && inv.verificationStatus !== 'VERIFIED' && inv.verificationStatus !== 'OVERRIDDEN' && (
                                  <Tooltip label="Verify this invoice against the order">
                                    <Button size="sm" variant="text" aria-label="Verify this invoice against the order" icon={<ShieldCheck size={16} />}
                                      loading={verifyInvoice.isPending && verifyInvoice.variables === String(inv.id)}
                                      onClick={() => verifyInvoice.mutate(String(inv.id))} />
                                  </Tooltip>
                                )}
                                {canOverride && inv.verificationStatus === 'MISMATCH' && (
                                  <Tooltip label="Approve the mismatch">
                                    <Button size="sm" variant="soft-danger" aria-label="Approve the mismatch" icon={<AlertTriangle size={16} />} onClick={() => setOverrideFor(String(inv.id))} />
                                  </Tooltip>
                                )}
                                {canVoidInvoice && (
                                  <Tooltip label="Void (cancel) this invoice">
                                    <Popconfirm title="Void (cancel) this invoice?" description="Cancels the invoice and reverses its ledger debit. You can upload a corrected invoice afterwards." okText="Void invoice" okButtonProps={{ danger: true }} onConfirm={() => voidInvoice.mutate(String(inv.id))}>
                                      <Button size="sm" variant="soft-danger" aria-label="Void (cancel) this invoice" icon={<Ban size={16} />} loading={voidInvoice.isPending && voidInvoice.variables === String(inv.id)} />
                                    </Popconfirm>
                                  </Tooltip>
                                )}
                              </Space>
                            );
                          },
                        }] : []),
                      ]}
                    />
                  )}
                  </DocSection>
                  {(orderNotes?.length ?? 0) > 0 && (
                    <DocSection icon={<FileCheck />} tone="violet" title={`Credit / debit notes (${orderNotes!.length})`}>
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={orderNotes}
                        onRow={(cn: any) => ({ onClick: () => setNoteDrawerId(String(cn.id)), style: { cursor: 'pointer' } })}
                        columns={[
                          { title: 'Note No', dataIndex: 'noteNo', render: (v: string) => <span className="font-medium text-brand-700 dark:text-brand-400">{v}</span> },
                          { title: 'Type', dataIndex: 'noteType', render: (v: string) => <Tag color={v === 'CREDIT' ? 'green' : 'gold'}>{v}</Tag> },
                          { title: 'Against invoice', dataIndex: 'invoiceNo', render: (v: string) => v || '—' },
                          { title: 'Date', dataIndex: 'noteDate', render: (v: any) => formatIstDate(v) },
                          { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'CANCELLED' ? 'red' : 'green'}>{v === 'CANCELLED' ? 'Reversed' : v}</Tag> },
                          { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (v: any, cn: any) => <b className={cn.status === 'CANCELLED' ? 'text-subtle line-through' : undefined}>{inr2(v)}</b> },
                        ]}
                      />
                    </DocSection>
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>

      </div>{/* ── main column ends ─────────────────────────────────────────────────────────── */}

      {/* THE RAIL — read-once reference, kept in view while the main column scrolls. */}
      {canSeeMoney && (
      <aside className="min-w-0 space-y-3 lg:col-span-4">
        <div className="space-y-3 lg:sticky lg:top-3">
          {canSeeMoney && (
            <Card title="Totals">
              <AmountRow label="Subtotal" value={inr2(o.subtotal ?? 0)} />
              <AmountRow label={`Discount${discountPctLabel}`} value={discountTotal > 0 ? `− ${inr2(discountTotal)}` : inr2(0)} tone={discountTotal > 0 ? 'danger' : undefined} />
              {/* WHICH tax, not just how much: one "Tax ₹514" line hides whether this is one head or
                  two, and the split is what gets keyed into Tally. Rates are derived from the taxable
                  base, so a mixed-rate order shows its blended effective rate rather than a wrong one. */}
              {Number(o.igstAmt ?? 0) > 0 ? (
                <AmountRow label={`IGST${gstRate(o.igstAmt, o.taxableAmt)}`} value={inr2(o.igstAmt)} />
              ) : (
                <>
                  <AmountRow label={`CGST${gstRate(o.cgstAmt, o.taxableAmt)}`} value={inr2(o.cgstAmt)} />
                  <AmountRow label={`SGST${gstRate(o.sgstAmt, o.taxableAmt)}`} value={inr2(o.sgstAmt)} />
                </>
              )}
              {Math.abs(Number(o.roundOff ?? 0)) >= 0.005 && (
                <AmountRow label="Round off" value={`${Number(o.roundOff) < 0 ? '−' : '+'}${inr2(Math.abs(Number(o.roundOff)))}`} />
              )}
              <div className="my-1 border-t border-line" />
              <AmountRow strong label="Grand total" value={inr2(o.grandTotal ?? 0)} />
              {/* The prepaid net is a SECOND figure, not a replacement: the official lines stay gross
                  until the CD locks, so both have to be visible or the card cannot be reconciled. */}
              {o.advanceCdStatus === 'ESTIMATED' && (
                <div className="mt-1 type-caption text-muted">
                  Net payable <b className="text-content">{inr2(o.netGrandTotal ?? o.grandTotal)}</b> if prepaid — the advance discount applies on payment.
                </div>
              )}
            </Card>
          )}

          {/* Can the dealer be asked to pay yet? Only once the warehouse has accepted and a proforma exists. */}
          {canSeeMoney && <ProformaCard orderId={String(o.id)} />}

          {/* Why the Tax figure above is CGST+SGST or IGST — shown to the roles that answer for it. */}
          {canSeeMoney && <GstDeterminationCard orderId={String(o.id)} />}
        </div>
      </aside>
      )}
      </div>{/* ── two-column grid ends ────────────────────────────────────────────────────── */}

      <CreditNoteModal id={noteDrawerId} onClose={() => setNoteDrawerId(null)} />

      <DispatchModal open={dispatchOpen} onClose={() => setDispatchOpen(false)} orderId={id} />
      {/* Hold for stock — the reason is the point of the dialog, not a formality: it is written to the
          status history, the audit trail and the message the dealer receives. */}
      <Modal
        open={backorderOpen}
        onOpenChange={(open) => { if (!open) { setBackorderOpen(false); setBackorderReason(''); } }}
        title="Hold this order for stock"
        width={460}
        footer={
          <>
            <Button variant="text" onClick={() => { setBackorderOpen(false); setBackorderReason(''); }}>Cancel</Button>
            <Button variant="primary" loading={backorder.isPending} onClick={() => backorder.mutate(backorderReason)}>
              Hold for stock
            </Button>
          </>
        }
      >
        <Alert type="info" style={{ marginBottom: 12 }}
          title="The order stays live — it just stops waiting on the warehouse."
          description="The dealer and their salesperson are told it is held and why. Nothing is reserved: when stock arrives you will be notified, and you decide then whether this order gets it." />
        <Textarea rows={3} value={backorderReason} onChange={(e) => setBackorderReason(e.target.value)} autoFocus
          placeholder="What is it waiting for? (e.g. next production run of AGN 1L — visible to the dealer and in the audit trail)" />
      </Modal>

      <CancelOrderModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        orderNo={o.orderNo}
        breakdown={cancelBreakdown}
        fmtQty={fmtQty}
        canFullCancel={canFullCancelNow}
        fullCancelBlockedByInvoice={fullCancelBlockedByInvoice}
        onFullCancel={(reason) => change.mutate({ status: 'CANCELLED', notes: reason })}
        // Mirrors the backend rule: cancelling an order the warehouse was already told to fulfil must say why.
        reasonRequired={['APPROVED', 'ACCEPTED', 'PACKED'].includes(o.status) || cancelNeedsApproval}
        needsApproval={cancelNeedsApproval}
        pendingRequest={o.cancelRequest ?? null}
        fullCancelLoading={change.isPending && (change.variables as any)?.status === 'CANCELLED'}
        canShortClose={canShortCloseNow}
        shortCloseHint={shortCloseHint}
        onShortClose={(reason) => shortClose.mutate(reason)}
        shortCloseLoading={shortClose.isPending}
      />
      <PayOrderModal open={payOpen} onClose={() => setPayOpen(false)} orderId={String(id)} orderNo={o.orderNo} />
      <SettleAdvanceModal
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        orderId={String(id)}
        orderNo={o.orderNo}
        excess={Number(advanceExcess?.excess ?? 0)}
        billed={Number(advanceExcess?.billed ?? 0)}
        paidNet={Number(advanceExcess?.paidNet ?? 0)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['order-advance-excess', id] });
          qc.invalidateQueries({ queryKey: ['order-payments', id] });
          qc.invalidateQueries({ queryKey: ['order-pay-summary', id] });
          qc.invalidateQueries({ queryKey: ['order-timeline', id] });
          qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
        }}
      />
      {proofView && (
        <AttachmentViewer
          files={proofView.files}
          index={proofAt}
          label={proofView.label}
          onIndex={setProofAt}
          onClose={() => { setProofView(null); setProofAt(0); }}
        />
      )}
      {/* Verifying a payment from the order it belongs to. The dialog owns the whole decision —
          proof, UTR, which bank it landed in, approve or reject with a reason — and invalidates
          `order-payments` itself, so this row refreshes without any bookkeeping here. */}
      <PaymentReviewModal open={!!payReviewFor} payment={payReviewFor} onClose={() => setPayReviewFor(null)} />
      <RefundPaymentModal
        payment={refundFor}
        orderNo={o.orderNo}
        onClose={() => setRefundFor(null)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['order-payments', id] });
          qc.invalidateQueries({ queryKey: ['order-pay-summary', id] });
          qc.invalidateQueries({ queryKey: ['order-timeline', id] });
          qc.invalidateQueries({ queryKey: ['payments'] });
          qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
        }}
      />
      <InvoiceUploadModal
        open={invoiceUploadOpen}
        onClose={() => setInvoiceUploadOpen(false)}
        presetOrder={{
          id, orderNo: o.orderNo, retailerId: o.retailerId, grandTotal: o.grandTotal, orderedQty: totalOrdered, pendingQty: totalPending,
          taxableAmt: o.taxableAmt, cgstAmt: o.cgstAmt, sgstAmt: o.sgstAmt, igstAmt: o.igstAmt, taxNature: o.gstTaxNature,
        }}
      />
      <InvoiceUploadModal
        open={!!invoiceForShipment}
        onClose={() => setInvoiceForShipment(null)}
        presetShipment={invoiceForShipment ? { id: String(invoiceForShipment.id), shipmentNo: invoiceForShipment.shipmentNo, orderId: id, orderNo: o.orderNo, retailerId: o.retailerId, taxNature: o.gstTaxNature } : null}
      />
      <RaiseReturnModal open={returnOpen} onClose={() => setReturnOpen(false)} order={o} invoices={invoices ?? []} shipments={shipments ?? []} />

      {/* Pick which packed dispatch to invoice — only when the header shortcut finds 2+ awaiting one. */}
      <Modal open={invoicePickerOpen} onOpenChange={(nextOpen) => { if (!nextOpen) setInvoicePickerOpen(false); }} title="Upload invoice — choose the shipment">
        <div className="text-sm" style={{ color: token.colorTextSecondary, marginBottom: 8 }}>
          More than one packed shipment is awaiting a Tally invoice. Each shipment is billed separately — pick the one to invoice.
        </div>
        {shipmentsNeedingInvoice.map((s: any) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderTop: '1px solid var(--gb-surface-2)' }}>
            <span><b>{s.shipmentNo}</b> <Tag>{s.status}</Tag></span>
            <Button variant="primary" size="sm" icon={<Upload size={16} />} onClick={() => { setInvoicePickerOpen(false); setInvoiceForShipment(s); }}>Upload for this</Button>
          </div>
        ))}
      </Modal>
      <ApproveOrderModal
        open={approveOpen}
        orderId={id ?? null}
        orderNo={o.orderNo}
        defaultWarehouseId={o.dispatchWarehouseId}
        onClose={() => setApproveOpen(false)}
      />
      <Modal
        open={!!overrideFor}
        title="Approve invoice mismatch"
        onOpenChange={(nextOpen) => { if (!nextOpen) { setOverrideFor(null); setOverrideReason(''); } }}
        footer={
          <>
            <Button variant="default" onClick={() => { setOverrideFor(null); setOverrideReason(''); }}>Cancel</Button>
            <Button variant="danger" loading={overrideInvoice.isPending} disabled={!overrideReason.trim()} onClick={() => overrideFor && overrideInvoice.mutate({ invId: overrideFor, reason: overrideReason.trim() })}>Approve & unblock dispatch</Button>
          </>
        }
      >
        <Typography.Paragraph type="secondary">
          The Tally invoice totals differ from the order. Approving records your reason (audit) and lets the order dispatch.
        </Typography.Paragraph>
        <Input.TextArea rows={3} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="Reason for approving the mismatch (mandatory)" />
      </Modal>

      {timeline && timeline.length > 0 && (
        <div className="text-xs" style={{ marginTop: 8, color: token.colorTextTertiary }}>
          <Clock size={16} /> Last activity {formatIst(timeline[timeline.length - 1].at)}
        </div>
      )}
    </div>
  );
}

/**
 * GST DETERMINATION — how this order was classified, and on what facts.
 *
 * Accounts opens this when a dealer asks why a bill says IGST. It shows the three facts the decision
 * is made from (who supplied, where the supply went, what that makes it) rather than an answer alone,
 * because "IGST" on its own is exactly what nobody can check. A FROZEN order answers from the snapshot
 * taken when its statutory invoice was raised, so the panel keeps telling the truth about old
 * documents after the masters have moved on.
 */
function GstDeterminationCard({ orderId }: { orderId: string }) {
  const canSee = usePermission('GST.DETERMINATION.VIEW');
  const { data } = useQuery({
    queryKey: ['gst-determination', orderId],
    queryFn: () => request<any>({ url: `/gst/orders/${orderId}/determination` }),
    enabled: canSee && !!orderId,
  });
  if (!canSee || !data) return null;

  // The three things a reader needs, in the order they need them: WHAT the tax is, WHERE the goods
  // move between (which is the whole reason), and WHO decided it and when. The machine reason
  // (`SUPPLIER_29_TO_POS_27`) is kept for the audit trail but demoted to a tooltip — it explained
  // nothing to the person reading the bill, and it was the largest thing on the card.
  const NATURE: Record<string, { label: string; heads: string; tone: 'green' | 'gold' }> = {
    INTRA_STATE: { label: 'Intra-state supply', heads: 'CGST + SGST', tone: 'green' },
    INTRA_UT: { label: 'Intra-union-territory supply', heads: 'CGST + UTGST', tone: 'green' },
    INTER_STATE: { label: 'Inter-state supply', heads: 'IGST', tone: 'gold' },
  };
  const STATUS_NOTE: Record<string, string> = {
    AUTHORITATIVE: 'Settled — this is what the order is billed on.',
    PROVISIONAL: 'Estimated until a fulfillment warehouse is chosen at approval.',
    FROZEN: 'Locked by the tax invoice — never recomputed.',
    BLOCKED: 'Cannot be determined yet.',
  };
  const STATUS_TONE: Record<string, 'green' | 'gold' | 'red' | 'neutral'> = {
    AUTHORITATIVE: 'green', FROZEN: 'green', PROVISIONAL: 'gold', BLOCKED: 'red',
  };
  const blockers: { code: string; message: string; fix?: string }[] = data.blockers ?? [];
  const nature = data.taxNature ? NATURE[data.taxNature] : null;
  const supplierState = data.supplierStateName
    ? `${data.supplierStateName} (${data.supplierStateCode})` : (data.supplierStateCode ?? null);
  const posState = data.placeOfSupplyStateName
    ? `${data.placeOfSupplyStateName} (${data.placeOfSupplyStateCode})` : (data.placeOfSupplyStateCode ?? null);

  // The verdict's own colour, carried by the banner as well as the chip — inter-state (IGST) and
  // intra-state (two heads) are different answers and should not look identical at a glance.
  const BANNER = nature?.tone === 'gold'
    ? 'border-harvest-400/40 bg-gradient-to-br from-harvest-400/15 to-transparent'
    : nature
      ? 'border-brand-500/30 bg-gradient-to-br from-brand-500/12 to-transparent'
      : 'border-line bg-surface-2';
  const BANNER_INK = nature?.tone === 'gold' ? 'text-harvest-700 dark:text-harvest-300' : nature ? 'text-brand-700 dark:text-brand-300' : 'text-muted';

  return (
    <Card
      title={<span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-4 text-brand-600" /> GST determination</span>}
      extra={<Tag tone={STATUS_TONE[data.status] ?? 'neutral'}>{String(data.status ?? '').toLowerCase()}</Tag>}
    >
      {/* THE ANSWER, first and largest — on a banner tinted by the verdict itself. */}
      <div className={cn('mb-3 rounded-card border p-3', BANNER)}>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <ArrowLeftRight className={cn('size-4 shrink-0', BANNER_INK)} />
          {nature
            ? <>
              <span className="type-section-title text-content">{nature.label}</span>
              <Tag tone={nature.tone}>{nature.heads}</Tag>
            </>
            : <span className="type-section-title text-muted">Not determined</span>}
        </div>
        <div className="mt-1 type-caption text-muted">{STATUS_NOTE[data.status] ?? ''}</div>
      </div>

      {/* THE REASON, as movement rather than as a code.
          A CONTAINER query, not a viewport one: this card sits in a ~310px rail on a 1024px screen, so
          `sm:` (which asks about the window) laid two boxes side by side in a third of the width and
          pushed the page into horizontal scroll. */}
      <div className="@container">
      <div className="grid items-stretch gap-3 grid-cols-[1fr_auto_1fr] @max-[26rem]:grid-cols-1">
        {/* Two ENDS of a journey, so they are coloured as two different places rather than as two
            identical grey boxes: green is where the goods leave from, sky is where they land. */}
        <div className="min-w-0 rounded-card border border-brand-500/25 bg-brand-500/8 p-3">
          <div className="inline-flex items-center gap-1.5 type-overline text-brand-700 dark:text-brand-400">
            <Store className="size-3.5" /> Supplied by
          </div>
          <div className="mt-1 break-words type-body-strong text-content">{data.supplierLegalName ?? '—'}</div>
          <div className="text-sm text-content">{supplierState ?? <span className="text-subtle">no registration</span>}</div>
          {data.supplierGstin && <div className="mt-0.5 break-all font-mono text-xs text-muted">{data.supplierGstin}</div>}
        </div>
        <div className="flex items-center justify-center px-1">
          {/* Stacked, the movement reads downwards — an arrow still pointing right would contradict
              the layout it sits in. */}
          <span className="grid size-7 place-items-center rounded-full bg-surface-3 text-subtle">
            <ArrowRight size={16} className="@max-[26rem]:hidden" />
            <ArrowDown size={16} className="hidden @max-[26rem]:block" />
          </span>
        </div>
        <div className="min-w-0 rounded-card border border-sky-500/25 bg-sky-500/8 p-3">
          <div className="inline-flex items-center gap-1.5 type-overline text-sky-700 dark:text-sky-400">
            <MapPin className="size-3.5" /> Delivered to (place of supply)
          </div>
          <div className="mt-1 break-words type-body-strong text-content">{posState ?? '—'}</div>
          <div className="text-sm text-muted">
            {data.placeOfSupplySource === 'SHIP_TO' ? 'From the delivery address'
              : data.placeOfSupplySource === 'BILL_TO' ? "From the retailer's address" : '—'}
          </div>
          {data.recipientGstin && <div className="mt-0.5 break-all font-mono text-xs text-muted">{data.recipientGstin}</div>}
        </div>
      </div>
      </div>

      {/* THE SENTENCE. What a person would say if asked why the bill looks like this — set on its own
          quiet strip so it reads as the explanation of the two boxes above, not as more of them. */}
      {nature && supplierState && posState && (
        <p className="mt-3 rounded-card border border-line bg-surface-2/60 px-3 py-2.5 text-sm text-muted">
          Goods move from <b className="text-content">{data.supplierStateName ?? data.supplierStateCode}</b> to{' '}
          <b className="text-content">{data.placeOfSupplyStateName ?? data.placeOfSupplyStateCode}</b>
          {data.taxNature === 'INTER_STATE'
            ? ' — different states, so this is an inter-state supply and IGST applies.'
            : ' — the same state, so this is an intra-state supply and it is split into two heads.'}
          {data.determinedAt && (
            <span className="text-subtle" title={data.reason ? `Audit reference: ${data.reason}` : undefined}>
              {' '}Determined {formatIst(data.determinedAt)}.
            </span>
          )}
        </p>
      )}
      {blockers.length > 0 && (
        <Alert
          className="mt-3"
          type={data.status === 'BLOCKED' ? 'error' : 'info'}
          message={data.status === 'BLOCKED' ? 'This order cannot be taxed until these are fixed' : 'Pending'}
          description={
            <ul className="mt-1 space-y-1">
              {blockers.map((b) => (
                <li key={b.code}>{b.message}{b.fix ? <span className="text-muted"> — {b.fix}</span> : null}</li>
              ))}
            </ul>
          }
        />
      )}
    </Card>
  );
}

/**
 * THE PROFORMA — issued, or not yet, and what that means for asking the dealer to pay.
 *
 * The sales desk's real question on this screen is "can I ask for the advance?", and the answer is
 * only yes once the order is approved with a fulfillment warehouse and a proforma exists. Before that
 * the document is a quotation, so the card says so rather than leaving a Download button that quietly
 * produces a different kind of paper than the reader expects.
 */
function ProformaCard({ orderId }: { orderId: string }) {
  const { data } = useQuery({
    queryKey: ['proforma-status', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/proforma-status` }),
    enabled: !!orderId,
  });
  // Nothing to say when the workflow is off: the proforma has always been printable on demand.
  if (!data?.enabled) return null;

  // What the advance discount is actually worth, in rupees — the percentage alone makes a reader do
  // the subtraction the card is in a position to do for them.
  const saving = data.grandTotal != null && data.netPayable != null ? Number(data.grandTotal) - Number(data.netPayable) : null;

  return (
    <Card
      title={<span className="inline-flex items-center gap-1.5"><FileCheck className="size-4 text-brand-600" /> Proforma</span>}
      extra={data.issued
        ? <Tag tone="green">Issued</Tag>
        : <Tag tone="gold">Not issued</Tag>}
    >
      {data.issued ? (
        <div className="space-y-3">
          {/* The document itself, on its own tinted strip — a proforma number is a piece of paper the
              dealer will quote back at you, so it gets the weight of one instead of being a grey
              label/value pair among others. */}
          <div className="rounded-card border border-brand-500/25 bg-brand-500/8 p-3">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0">
                <div className="type-overline text-brand-700 dark:text-brand-400">Proforma no</div>
                <div className="mt-0.5 break-all font-mono type-body-strong text-content">
                  {data.proformaNo}{data.version > 1 ? <span className="ml-1 text-muted">v{data.version}</span> : null}
                </div>
              </div>
              <div className="min-w-0">
                <div className="type-overline text-brand-700 dark:text-brand-400">Issued on</div>
                {/* A date-only field: formatIst would append a meaningless "12:00 AM". */}
                <div className="mt-0.5 whitespace-nowrap type-body-strong text-content">
                  {new Date(`${String(data.issueDate).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>
            <div className="mt-1.5 type-caption text-subtle">the date the discount and credit terms were set on</div>
          </div>

          {/* The one number the sales desk is here for. It is money the dealer can still save, so it
              is the loudest thing on the card rather than a third label in a row. */}
          {Number(data.cdPct) > 0 && (
            <div className="rounded-card border border-brand-500/30 bg-gradient-to-br from-brand-500/12 to-transparent p-3">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5 type-overline text-brand-700 dark:text-brand-400">
                  <Wallet className="size-3.5" /> If paid in advance
                </span>
                <Tag tone="green">{data.cdPct}% off</Tag>
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="type-metric-sm text-brand-700 dark:text-brand-300">{inr2(data.netPayable)}</span>
                {/* NO strikethrough on the grand total. Everywhere else in this app a struck-through
                    figure means CANCELLED or SUPERSEDED (a voided credit note, the old value in an audit
                    diff). This one is neither: the advance discount is conditional and unearned until a
                    verified payment covers the net, and if none arrives the order bills at exactly this
                    number — which the panel on the left says in so many words. Striking it claimed the
                    opposite of what is true. */}
                {saving != null && saving > 0 && (
                  <span className="type-caption text-muted">
                    saves <b className="text-content">{inr2(saving)}</b> on the {inr2(data.grandTotal)} grand total
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          message="No proforma yet — nothing to pay against"
          description={data.pending ?? 'The proforma is issued when this order is approved and a fulfillment warehouse is chosen.'}
        />
      )}
    </Card>
  );
}
